import React from "react";
import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import posthog from "posthog-js";
import { SEOHead } from "@/components/SEOHead";
import { friendlyErrorMessage } from "@/lib/friendlyError";
import { useCodeWorkspace } from "@/hooks/useCodeWorkspace";
import { useAICodeAgent } from "@/hooks/useAICodeAgent";
import { useSandboxPreview } from "@/hooks/useSandboxPreview";
import { useSelfHealingPreview, type UseSelfHealingPreviewOptions } from "@/hooks/useSelfHealingPreview";
import { useProjectGraph } from "@/hooks/useProjectGraph";
import { useVercelDeploy } from "@/hooks/useVercelDeploy";
import { useDeploymentHistory } from "@/hooks/useDeploymentHistory";
import { useAuth } from "@/contexts/AuthContext";
import { useCreateProjectFromStartup } from "@/hooks/useCreateProjectFromStartup";
import { useProjectSnapshots, ProjectSnapshot } from "@/hooks/useProjectSnapshots";
import { usePreviewCredentials } from "@/hooks/usePreviewCredentials";
import { BuilderEntryState } from "@/components/code-workspace/BuilderEntryState";
import { BuilderBuildingState } from "@/components/code-workspace/BuilderBuildingState";
import { BuilderWorkspaceState } from "@/components/code-workspace/BuilderWorkspaceState";
import { BuilderErrorBoundary } from "@/components/BuilderErrorBoundary";
import type { SmokeTestState, SmokeTestStep } from "@/components/code-workspace/SmokeTestPanel";
import { BuilderDebugHUD } from "@/components/code-workspace/BuilderDebugHUD";
import { CustomDomainPanel } from "@/components/code-workspace/CustomDomainPanel";
import { toast } from "sonner";
import {
  buildSandbox,
  normalizeFileMapForSandbox,
  getBuildErrorBucket,
  getBuildErrorSnippet,
} from "@/lib/sandboxBuild";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, ExternalLink, Check, Globe, XCircle, Key } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { downloadProjectAsZip } from "@/lib/downloadProjectZip";
import { filterFilesForPush } from "@/lib/githubPush";
import { useGitHubConnection } from "@/hooks/useGitHubConnection";
import { getDayOneAppFrameworkScaffold } from "@/lib/dayoneAppFrameworkScaffold";
import { getOrCreateActiveSessionId } from "@/lib/activeSessionId";
import { guardAndCoalescePatches, MAX_PATCH_ITEMS, normalizePathExtension, rewritePatchPathsToMatchTargets } from "@/lib/patchGuard";
import {
  validateBuilderPatch,
  patchItemsToEdits,
  editsToRaw,
  normalizePathForPatch,
  type RejectedItem,
} from "@/lib/patchValidation";
import { filterEditsForLanding } from "@/lib/landingFilter";
import { logBuildErrorClient } from "@/lib/buildErrorLogger";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/lib/fetchJson";
import { useSubscription } from "@/hooks/useSubscription";
import { PaywallModal } from "@/components/PaywallModal";

type DevProject = { id: string; name: string };

const PLAN_STORAGE_KEY = (projectId: string) => `dayone.plan.${projectId}`;
const AUTO_DEPLOY_KEY = (projectId: string) => `dayone:autoDeploy:${projectId}`;
const CUSTOM_DOMAIN_KEY = (projectId: string) => `dayone:customDomain:${projectId}`;
const TELEMETRY_KEY_PREFIX = "dayone:sandbox:telemetry:";
const PLAN_SUMMARY_MAX_CHARS = 1200;

function planToSummary(plan: {
  problem?: string;
  icp?: string;
  mvpFeatures?: string[];
  monetization?: string;
  keyScreens?: string[];
  dataModel?: string[];
  fileTree?: string[];
}): string {
  const lines: string[] = [];
  if (plan.problem) lines.push(`Problem: ${plan.problem}`);
  if (plan.icp) lines.push(`ICP: ${plan.icp}`);
  if (plan.mvpFeatures?.length) lines.push(`MVP: ${plan.mvpFeatures.join(", ")}`);
  if (plan.monetization) lines.push(`Monetization: ${plan.monetization}`);
  if (plan.keyScreens?.length) lines.push(`Screens: ${plan.keyScreens.join(", ")}`);
  if (plan.dataModel?.length) lines.push(`Data: ${plan.dataModel.join(", ")}`);
  if (plan.fileTree?.length) lines.push(`Files: ${plan.fileTree.join(", ")}`);
  return lines.join("\n");
}

/** VAGUE_PROMPT_ENFORCEMENT: detect prompts that must go through generate-plan before file-targets. */
function isVaguePrompt(message: string): boolean {
  const t = (message ?? "").trim();
  if (t.length < 80) return true;
  const lower = t.toLowerCase();
  if (/\bclone\b/.test(lower)) return true;
  if (/\bsimilar\b/.test(lower)) return true;
  if (/\b(build|make|create)\b.*\b(like|similar to)\b/i.test(t)) return true;
  if (/\bsomething like\b/i.test(t)) return true;
  return false;
}

/**
 * Detect vague fix/edit requests that lead to over-editing.
 * Returns true if the user prompt is a fix request that lacks specificity.
 * Only applies to edit mode (existing files), not first builds.
 *
 * Only triggers on SHORT vague requests (< 10 words) with NO specific elements.
 * Does NOT trigger on requests like "the sign in button doesn't work fix that and add a sign up section"
 * because that mentions specific elements ("sign in button", "sign up section").
 */
function isVagueFixRequest(message: string): boolean {
  const t = (message ?? "").trim().toLowerCase();

  // Count words - if 10+ words, it's specific enough
  const wordCount = t.split(/\s+/).filter(w => w.length > 0).length;
  if (wordCount >= 10) return false;

  // Check for specific element names - if present, it's NOT vague
  const specificElements = /\b(button|header|footer|navbar|nav|sidebar|modal|dialog|form|input|field|text|label|title|card|table|list|menu|dropdown|select|checkbox|radio|toggle|switch|tab|panel|section|page|screen|link|image|icon|avatar|badge|alert|toast|notification|chart|graph|color|font|size|margin|padding|border|background|style|layout|grid|flex|column|row|width|height|sign\s*in|sign\s*up|login|logout|register|submit|save|delete|edit|add|remove|update|create|search|filter|sort|profile|settings|dashboard|home|detail|view)\b/.test(t);
  if (specificElements) return false;

  // Must look like a fix request (not a new feature request)
  const isFixIntent = /\b(fix|broken|doesn'?t work|not working|isn'?t working|won'?t work|stopped working|bug|wrong|issue|problem)\b/.test(t);
  if (!isFixIntent) return false;

  // Only these ultra-vague patterns trigger the guard
  const vaguePatterns = [
    /^fix\s+(it|this|that|everything|the app|the page|my app)\s*\.?$/i,  // "fix it", "fix everything"
    /^(it'?s?|everything'?s?)\s+(broken|wrong|buggy)\s*\.?$/i,  // "it's broken"
    /^(nothing|stuff|things)\s+(works?|working)\s*\.?$/i,  // "nothing works"
    /^make\s+it\s+(work|better)\s*\.?$/i,  // "make it work"
    /^something'?s?\s+(wrong|broken)\s*\.?$/i,  // "something's wrong"
  ];
  return vaguePatterns.some((re) => re.test(t));
}

const VAGUE_FIX_RESPONSE = "Which specific element isn't working? Please describe exactly what you clicked and what happened (or didn't happen). For example: \"The 'Add Task' button on the dashboard doesn't add a new item\" or \"The save form shows an error when I submit.\"";


/** Centralized builder pipeline status for UI and guards. */
type BuilderStatus =
  | "idle"
  | "generating"
  | "applying"
  | "building"
  | "fixing"
  | "polishing"  // Auto-fix in progress (hidden from user, shows friendly message)
  | "success"
  | "failed";

/** Build Timeline: one entry per builder run (last 5). */
export type BuildLogItem = {
  id: string;
  ts: number;
  status: "generating" | "applying" | "building" | "fixing" | "polishing" | "success" | "failed";
  prompt?: string;
  summary?: string;
  error?: string;
};

// ---------------------------------------------------------------------------
// API-key detection: scan generated code for placeholder strings
// ---------------------------------------------------------------------------
interface DetectedPlaceholder {
  service: string;
  keyName: string;
  placeholder: string;
  reason: string;
}

const PLACEHOLDER_PATTERNS: { pattern: RegExp; service: string; keyName: string; reason: string }[] = [
  { pattern: /YOUR_STRIPE[_A-Z]*KEY[_A-Z]*HERE/g, service: "Stripe", keyName: "STRIPE_PUBLISHABLE_KEY", reason: "payments" },
  { pattern: /YOUR_OPENAI[_A-Z]*KEY[_A-Z]*HERE/g, service: "OpenAI", keyName: "OPENAI_API_KEY", reason: "AI features" },
  { pattern: /YOUR_ANTHROPIC[_A-Z]*KEY[_A-Z]*HERE/g, service: "Anthropic", keyName: "ANTHROPIC_API_KEY", reason: "AI features" },
  { pattern: /YOUR_GOOGLE[_A-Z]*MAPS?[_A-Z]*KEY[_A-Z]*HERE/g, service: "Google Maps", keyName: "GOOGLE_MAPS_API_KEY", reason: "maps" },
  { pattern: /YOUR_MAPBOX[_A-Z]*TOKEN[_A-Z]*HERE/g, service: "Mapbox", keyName: "MAPBOX_ACCESS_TOKEN", reason: "maps" },
  { pattern: /YOUR_TWILIO[_A-Z]*KEY[_A-Z]*HERE/g, service: "Twilio", keyName: "TWILIO_API_KEY", reason: "SMS" },
  { pattern: /YOUR_SENDGRID[_A-Z]*KEY[_A-Z]*HERE/g, service: "SendGrid", keyName: "SENDGRID_API_KEY", reason: "email" },
  { pattern: /YOUR_RESEND[_A-Z]*KEY[_A-Z]*HERE/g, service: "Resend", keyName: "RESEND_API_KEY", reason: "email" },
  { pattern: /YOUR_GOOGLE[_A-Z]*CLIENT[_A-Z]*ID[_A-Z]*HERE/g, service: "Google OAuth", keyName: "GOOGLE_CLIENT_ID", reason: "Google sign-in" },
  { pattern: /YOUR_GITHUB[_A-Z]*CLIENT[_A-Z]*ID[_A-Z]*HERE/g, service: "GitHub OAuth", keyName: "GITHUB_CLIENT_ID", reason: "GitHub sign-in" },
  { pattern: /YOUR_OPENWEATHER[_A-Z]*KEY[_A-Z]*HERE/g, service: "OpenWeather", keyName: "OPENWEATHER_API_KEY", reason: "weather data" },
  { pattern: /YOUR_ALPHA[_A-Z]*VANTAGE[_A-Z]*KEY[_A-Z]*HERE/g, service: "Alpha Vantage", keyName: "ALPHA_VANTAGE_API_KEY", reason: "financial data" },
  { pattern: /YOUR_SPORTS?[_A-Z]*KEY[_A-Z]*HERE/g, service: "Sports API", keyName: "SPORTS_API_KEY", reason: "sports data" },
];

/** Catch-all for any YOUR_*_HERE placeholder not covered above */
const GENERIC_PLACEHOLDER_RE = /YOUR_([A-Z][A-Z0-9_]*?)_HERE/g;

function scanFilesForPlaceholders(files: { name: string; content: string }[]): DetectedPlaceholder[] {
  const found = new Map<string, DetectedPlaceholder>();
  const allContent = files.map(f => f.content).join("\n");

  for (const p of PLACEHOLDER_PATTERNS) {
    p.pattern.lastIndex = 0;
    const m = p.pattern.exec(allContent);
    if (m && !found.has(p.keyName)) {
      found.set(p.keyName, { service: p.service, keyName: p.keyName, placeholder: m[0], reason: p.reason });
    }
  }

  // Catch-all for unknown placeholders
  GENERIC_PLACEHOLDER_RE.lastIndex = 0;
  let gm: RegExpExecArray | null;
  while ((gm = GENERIC_PLACEHOLDER_RE.exec(allContent)) !== null) {
    const keyName = gm[1]; // e.g. "FIREBASE_API_KEY"
    if (!found.has(keyName)) {
      found.set(keyName, { service: keyName.replace(/_/g, " ").replace(/\bAPI\b/gi, "API"), keyName, placeholder: gm[0], reason: "external service" });
    }
  }

  return Array.from(found.values());
}

/** Scan files for import.meta.env.VITE_ references (env vars the app needs at runtime). */
function detectEnvVarUsage(files: { name: string; content: string }[]): string[] {
  const vars = new Set<string>();
  const re = /import\.meta\.env\.(VITE_[A-Z0-9_]+)/g;
  for (const f of files) {
    let m: RegExpExecArray | null;
    re.lastIndex = 0;
    while ((m = re.exec(f.content)) !== null) vars.add(m[1]);
  }
  return Array.from(vars);
}

/** Map VITE_ env var names to readable service names for chat messages. */
function envVarToServiceName(varName: string): string {
  const raw = varName.replace(/^VITE_/, "").toLowerCase();
  const services: [RegExp, string][] = [
    [/openai/, "OpenAI API"], [/anthropic/, "Anthropic API"], [/stripe/, "Stripe"],
    [/supabase.*url/, "Supabase URL"], [/supabase/, "Supabase"],
    [/firebase/, "Firebase"], [/google.*maps|maps/, "Google Maps"], [/google/, "Google API"],
    [/github/, "GitHub API"], [/twilio/, "Twilio"], [/sendgrid/, "SendGrid"],
    [/resend/, "Resend"], [/mapbox/, "Mapbox"], [/algolia/, "Algolia"],
    [/clerk/, "Clerk Auth"], [/pinecone/, "Pinecone"], [/replicate/, "Replicate"],
    [/aws/, "AWS"], [/plaid/, "Plaid"], [/notion/, "Notion API"],
    [/discord/, "Discord"], [/slack/, "Slack"], [/airtable/, "Airtable"],
    [/weather/, "Weather API"], [/openweather/, "OpenWeatherMap"],
    [/paypal/, "PayPal"], [/pusher/, "Pusher"], [/lemon|squeezy/, "LemonSqueezy"],
    [/cloudflare/, "Cloudflare"], [/redis/, "Redis"], [/mongo/, "MongoDB"],
  ];
  for (const [re, name] of services) {
    if (re.test(raw)) return name;
  }
  // Fallback: VITE_SOME_API_KEY → "Some API Key"
  return varName.replace(/^VITE_/, "").split("_")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** Detect if a chat message is asking to "make something real" / connect to actual APIs */
const REAL_API_INTENT_RE = /\b(make|connect|hook\s*up|wire\s*up|integrate|actually\s*work|real\s*data|real\s*api|real\s*payment|live\s*data|production|use\s*real|add\s*my\s*key|add\s*api\s*key|use\s*my\s*key)\b/i;

function isRealApiRequest(prompt: string): boolean {
  return REAL_API_INTENT_RE.test(prompt);
}

/** DayOne framework = scaffold framework files exist (main.tsx + lib/utils.ts = minimum viable scaffold). */
function hasDayOneFramework(files: Map<string, string>): boolean {
  return files.has("src/main.tsx") && files.has("src/lib/utils.ts");
}

/**
 * Check if a file path is a scaffold file (not user-created app content).
 * Mirrors the server's isScaffoldFile logic in builder-chat-openai/index.ts.
 * Used to detect first builds where the landing filter should be skipped.
 */
function isScaffoldPath(path: string, content?: string): boolean {
  const SCAFFOLD_ENTRYPOINTS = ["index.html", "src/main.tsx", "src/index.css", "src/vite-env.d.ts", "package.json", "vite.config.ts", "tsconfig.json"];
  if (SCAFFOLD_ENTRYPOINTS.includes(path)) return true;
  if (path.startsWith("src/components/ui/")) return true;
  if (path.startsWith("src/lib/")) return true;
  if (path.startsWith("src/contexts/")) return true;
  if (path.startsWith("src/data/")) return true; // Auto-stubs
  if (path.startsWith(".dayone/")) return true;
  // Landing page templates from generate-app-scaffold
  if (/^src\/components\/(Hero|Features|Testimonials|Pricing|Footer|Navbar)Section/i.test(path)) return true;
  if (/^src\/pages\/LandingPage/i.test(path)) return true;
  // Placeholder App.tsx from scaffold (contains "Building your app")
  if (path === "src/App.tsx" && typeof content === "string" && content.includes("Building your app")) return true;
  return false;
}

/**
 * Detect first build: App.tsx still has scaffold placeholder content.
 * Content-based detection avoids race condition with filesLoadedFromDB.
 *
 * Returns TRUE when:
 * - No files loaded yet (preload incomplete, but scaffold was just written)
 * - App.tsx contains "Building your app" placeholder
 *
 * Returns FALSE when:
 * - App.tsx has been replaced with real app content
 * - App.tsx doesn't exist but other files do (unusual state, be safe)
 */
function isFirstBuild(files: Map<string, string>): boolean {
  // No files loaded yet = initial build (scaffold hasn't been preloaded yet)
  // This handles the race condition: build starts before preload completes
  if (files.size === 0) {
    console.log("[isFirstBuild] files.size=0 → TRUE (preload incomplete, assume initial build)");
    return true;
  }

  // Check App.tsx content for scaffold placeholder
  const appContent = files.get("src/App.tsx");
  if (!appContent) {
    // Files exist but no App.tsx = unusual state, default to FALSE (safe)
    console.log("[isFirstBuild] App.tsx not found but other files exist → FALSE (unusual state)");
    return false;
  }

  // Scaffold placeholder marker from generate-app-scaffold
  const isScaffold = appContent.includes("Building your app");
  console.log(`[isFirstBuild] App.tsx ${isScaffold ? "has" : "does NOT have"} scaffold placeholder → ${isScaffold}`);
  return isScaffold;
}

const CodeWorkspace = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const debugEnabled = searchParams.get("debug") === "1";
  const actionFromUrl = searchParams.get("action");
  const projectIdFromUrl = searchParams.get("projectId");
  const prefillFromUrl = searchParams.get("prefill");
  const { canBuild, canUseCustomDomain, getDailyBuildCount, incrementBuildCount, tier, loading: subscriptionLoading } = useSubscription();
  const navState = location.state as { prefill?: string; autoBuild?: boolean; source?: "idea" | "create" } | null;
  const prefillFromState = navState?.prefill;
  const promptPreFill = prefillFromState ?? (prefillFromUrl ? (() => { try { return decodeURIComponent(prefillFromUrl); } catch { return prefillFromUrl; } })() : undefined);
  const autoBuildFromNav = navState?.autoBuild === true;
  const editorRef = useRef<any>(null);

  // DEBUG: Log navigation state on mount
  console.log("[CodeWorkspace] MOUNT/RENDER navState:", { source: navState?.source, autoBuild: navState?.autoBuild, prefill: navState?.prefill?.slice(0, 60), promptPreFill: promptPreFill?.slice(0, 60), prefillFromUrl: prefillFromUrl?.slice(0, 30) });

  const onboardingStorageKey = user?.id ? `dayone.onboarding.workspace.dismissed.${user.id}` : "dayone.onboarding.workspace.dismissed";

  const buildStartedThisSessionRef = useRef(false);
  const didAutoStartRef = useRef(false);

  const SCAFFOLD_WAIT_TIMEOUT_MS = 45000;
  const [scaffoldWaitTimedOut, setScaffoldWaitTimedOut] = useState(false);

  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

  const [devProject, setDevProject] = useState<DevProject | null>(null);

  const {
    files,
    projects,
    currentProject,
    loading,
    listProjects,
    selectProject,
    selectProjectById,
    renameProject,
    deleteProject,
    duplicateProject,
    listFiles,
    loadFile,
    saveFile,
    deleteFile,
    applyEdits,
  } = useCodeWorkspace();

  // CRITICAL: Don't use stale currentProject when URL has a different projectId
  // This prevents FK constraint errors when navigating from one project to another
  const activeProject = (projectIdFromUrl && currentProject?.id !== projectIdFromUrl)
    ? null
    : (currentProject || devProject);

  useEffect(() => {
    if (autoBuildFromNav && activeProject?.id && projectIdFromUrl === activeProject.id) {
      buildStartedThisSessionRef.current = true;
    }
  }, [autoBuildFromNav, activeProject?.id, projectIdFromUrl]);

  // ✅ deterministic sessionId: prefer project id, else persisted/generated active session
  const sessionId = getOrCreateActiveSessionId(activeProject?.id || projectIdFromUrl || null);

  const {
    messages: aiMessages,
    sendInstruction,
    dismissLastPlan,
    pushMessage,
    memory: projectMemory,
    isMemoryLoading,
    lastServerPatchMeta,
    lastRequestFlags,
    streamingProgress,
    planPreview,
    securityAlerts,
    clearSecurityAlerts,
    narrativeMessages,
  } = useAICodeAgent(activeProject?.id || null);

  const [projectLoadAttempted, setProjectLoadAttempted] = useState(false);
  const [projectLoadError, setProjectLoadError] = useState<string | null>(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  // API key collection state — never persisted, never logged
  const collectedApiKeysRef = useRef<Record<string, string>>({});
  const [pendingKeyPlaceholders, setPendingKeyPlaceholders] = useState<DetectedPlaceholder[]>([]);
  const pendingKeyEditRef = useRef<{ prompt: string; placeholders: DetectedPlaceholder[] } | null>(null);

  const { graph: projectGraph } = useProjectGraph();

  const {
    status: deployStatus, isDeploying, result: deployResult, logs: deployLogs, deploy, reset: resetDeploy,
    domainStatus, domainResult, addCustomDomain, checkDomainStatus, resetDomain,
  } = useVercelDeploy();

  const { deployments, addDeployment } = useDeploymentHistory(currentProject?.id || null);

  // Fetch preview credentials for auth in preview iframe
  const { hasCredentials: hasPreviewCreds } = usePreviewCredentials(activeProject?.id || null);

  // Last successful deploy URL for this project (from code_projects table)
  const lastDeployUrl = (currentProject as any)?.deploy_url ?? null;

  const { isCreating: isGeneratingProject, progress: generationProgress, createProjectFromPrompt } =
    useCreateProjectFromStartup();

  // Preview key forces iframe remount and cache-busted reload when incremented
  const [previewKey, setPreviewKey] = useState(0);
  const { isRunning: sandboxRunning, previewUrl, iframeRef, runPreview, error: sandboxError, consoleLogs: sandboxConsoleLogs } = useSandboxPreview({ previewKey, sessionId });
  const runtimeErrorSnippet = sandboxError?.trim().slice(0, 200) ?? null;

  // Track buildResetAt for logging purposes only — do NOT increment previewKey here.
  // The preview will be properly updated when runPreviewWithSession is called after build completes.
  // Incrementing previewKey during build causes race conditions with the boot effect.
  const lastBuildResetRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (streamingProgress.buildResetAt && streamingProgress.buildResetAt !== lastBuildResetRef.current) {
      lastBuildResetRef.current = streamingProgress.buildResetAt;
      console.log("[CodeWorkspace] buildResetAt changed — files cleared for new build");
      // Clear localStorage immediately so iframe shows skeleton on any subsequent reload
      if (sessionId) {
        try {
          localStorage.removeItem(`dayone:sandbox:files:${sessionId}`);
        } catch { /* ignore */ }
      }
    }
  }, [streamingProgress.buildResetAt, sessionId]);

  const { snapshots, isLoading: snapshotsLoading, createSnapshot, deleteSnapshot } =
    useProjectSnapshots(user ? currentProject?.id || null : null);

  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>("");
  const [editorContent, setEditorContent] = useState<string>("");
  const [loadedFileContents, setLoadedFileContents] = useState<Map<string, string>>(new Map());
  const [filesLoadedFromDB, setFilesLoadedFromDB] = useState(false);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [autoDeployEnabled, setAutoDeployEnabled] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallReason, setPaywallReason] = useState<"edit_denied" | "edit_limit" | "build_limit" | "publish_limit" | "generic">("generic");
  const [customDomainInput, setCustomDomainInput] = useState("");
  const [savedCustomDomain, setSavedCustomDomain] = useState<string | null>(null);
  const actionDeployFiredRef = useRef(false);

  // Sync auto-deploy and custom domain from localStorage when project changes
  useEffect(() => {
    if (!currentProject?.id) return;
    try {
      setAutoDeployEnabled(localStorage.getItem(AUTO_DEPLOY_KEY(currentProject.id)) === "true");
      setSavedCustomDomain(localStorage.getItem(CUSTOM_DOMAIN_KEY(currentProject.id)));
    } catch {
      setAutoDeployEnabled(false);
      setSavedCustomDomain(null);
    }
  }, [currentProject?.id]);

  const toggleAutoDeploy = useCallback((enabled: boolean) => {
    setAutoDeployEnabled(enabled);
    if (currentProject?.id) {
      try { localStorage.setItem(AUTO_DEPLOY_KEY(currentProject.id), String(enabled)); }
      catch { /* ignore */ }
    }
  }, [currentProject?.id]);

  const [githubConnectModalOpen, setGitHubConnectModalOpen] = useState(false);
  const [githubPushModalOpen, setGitHubPushModalOpen] = useState(false);
  const [githubConnectPatInput, setGitHubConnectPatInput] = useState("");
  const [githubPushRepoName, setGitHubPushRepoName] = useState("");
  const [githubPushPrivate, setGitHubPushPrivate] = useState(true);
  const [githubPushExistingOwner, setGitHubPushExistingOwner] = useState("");
  const [githubPushExistingRepo, setGitHubPushExistingRepo] = useState("");

  const {
    connection: githubConnection,
    linkedRepo: githubLinkedRepo,
    pushRunning: githubPushRunning,
    pushProgress: githubPushProgress,
    pushResult: githubPushResult,
    connectWithPat: githubConnectWithPat,
    disconnect: githubDisconnect,
    pushToGitHub,
    resetPushResult: githubResetPushResult,
  } = useGitHubConnection(activeProject?.id || null, user?.id);

  // Derive lastRepo from hook for backward-compatible prop
  const lastRepo = githubLinkedRepo
    ? {
        owner: githubLinkedRepo.repoFullName.split("/")[0],
        repo: githubLinkedRepo.repoFullName.split("/")[1],
        html_url: githubLinkedRepo.repoUrl,
      }
    : null;
  const [lastPreviewUpdatedAt, setLastPreviewUpdatedAt] = useState<number | null>(null);
  const [lastPreviewBootedAt, setLastPreviewBootedAt] = useState<number | null>(null);
  const [lastPreviewReadyAt, setLastPreviewReadyAt] = useState<number | null>(null);
  const [generationLogs, setGenerationLogs] = useState<string[]>([]);
  const [lastPrompt, setLastPrompt] = useState<string>("");
  const [lastApplySummary, setLastApplySummary] = useState<string | null>(null);

  // Centralized builder pipeline status (BuilderStateManager)
  const [builderStatus, setBuilderStatus] = useState<BuilderStatus>("idle");
  const [buildValidationError, setBuildValidationError] = useState<string | null>(null);
  const [buildValidationBucket, setBuildValidationBucket] = useState<string | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null); // Error hidden during polishing
  const [fixAttempted, setFixAttempted] = useState(false);

  // Chat mode: talk about the app without triggering builds
  const [chatMode, setChatMode] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);

  // Build Timeline (last 5 runs)
  const [buildLog, setBuildLog] = useState<BuildLogItem[]>([]);

  /** Smoke test state (?debug=1 only) */
  const [smokeTestState, setSmokeTestState] = useState<SmokeTestState>({
    status: "idle",
    steps: [],
  });

  /** Last file target plan from generate-file-targets (for debug HUD). */
  type FileTarget = { path: string; action: string; rationale?: string };
  const [lastFileTargets, setLastFileTargets] = useState<FileTarget[]>([]);

  /** Screen stubs created by sandbox build (in-memory only); shown as warning in preview panel. */
  const [createdScreenStubs, setCreatedScreenStubs] = useState<string[]>([]);

  /** allowCoreEdits override (debug-only); persists in localStorage. */
  const [allowCoreEdits, setAllowCoreEdits] = useState(() => {
    try { return localStorage.getItem("dayone:allowCoreEdits") === "true"; } catch { return false; }
  });

  /** Patch validation rejections from last apply (for debug HUD). */
  const [lastPatchValidation, setLastPatchValidation] = useState<{
    rejectedPatchItems: RejectedItem[];
    acceptedCount: number;
    rejectedCount: number;
    patchTotalChars: number;
  } | null>(null);

  /** Preflight build result: in-memory build before persisting patch. */
  const [lastPreflightResult, setLastPreflightResult] = useState<{
    ok: boolean;
    errorCount?: number;
    firstErrors?: string[];
  } | null>(null);

  /** Allowed file paths for the current AI run (enforced in handleApplyEdits). Cleared when build ends. */
  const allowedTargetPathsRef = useRef<Set<string> | null>(null);
  /** When set (DayOne), edits under these path prefixes are allowed in addition to exact target paths. */
  const allowedPrefixesRef = useRef<string[] | null>(null);

  /** Ref to latest handleApplyEdits so handleSendAIMessage can call it after await without it in deps. */
  const handleApplyEditsRef = useRef<(
    edits: any[],
    options?: { isFixAttempt?: boolean; skipFailToast?: boolean; simpleLandingIntent?: boolean }
  ) => Promise<any>>(null!);

  /** Run context for Build Report: set at start of run, consumed when apply completes. */
  type BuildReport = {
    runId: string;
    status: "success" | "failed";
    startedAt: number;
    finishedAt: number;
    userPromptSnippet: string;
    usedPlan: boolean;
    targetPaths: string[];
    changedPaths: string[];
    previewReadyAt?: number | null;
    errorBucket?: string | null;
    /** PatchGuard/apply error code when blocked (e.g. PATCH_GUARD_PROTECTED_FILE). */
    lastErrorCode?: string | null;
    /** Paths the AI tried to edit that were outside allowed targets (when errorBucket is OUT_OF_SCOPE_EDIT). */
    outOfScopePaths?: string[];
    /** Count of edits dropped by final landing scope gate this run. */
    landingScopeDroppedCount?: number;
    /** Sample of paths dropped by landing scope (up to 10). */
    landingScopeDroppedPathsSample?: string[];
    /** Preflight build meta (in-memory build before persist). */
    preflight?: { ok: boolean; errorCount?: number; firstErrors?: string[]; attemptedRetry?: boolean; retryOutcome?: "succeeded" | "failed" | "no_edits" };
  };
  const [buildReport, setBuildReport] = useState<BuildReport | null>(null);
  const runContextRef = useRef<{
    runId: string;
    startedAt: number;
    userPromptSnippet: string;
    userPrompt: string;
    usedPlan: boolean;
    targetPaths: string[];
    simpleLandingIntent?: boolean;
  } | null>(null);
  const shellPathsThisRunRef = useRef<string[]>([]);
  const handleSendAIMessageRef = useRef<((msg: string | { prompt: string; overrideSimpleLandingIntent?: boolean; fromRetry?: boolean }) => void) | null>(null);
  const handleRunSmokeTestRef = useRef<(() => void) | null>(null);
  /** Track last build runId we auto-ran smoke test for — avoid re-running for same build. */
  const lastAutoSmokeRunIdRef = useRef<string | null>(null);
  /** RunId of the last user-initiated Run (composer). Auto-smoke only for this run. */
  const lastUserInitiatedRunIdRef = useRef<string | null>(null);
  const smokeTestStateRef = useRef<SmokeTestState | null>(null);
  const run5xCancelledRef = useRef(false);
  const lastGuardFailureRef = useRef<{ errorCode?: string; maxPatchItems?: number }>({});
  /** Set when landing filter dropped edits this run; used for retry instruction. */
  const landingFilterDroppedRef = useRef<boolean>(false);
  /** Count of edits dropped by landing filter this run (for debug display). */
  const landingFilterDroppedCountRef = useRef<number>(0);
  /** Count of edits dropped by final landing scope gate this run. */
  const landingScopeDroppedCountRef = useRef<number>(0);
  /** Sample of paths dropped by landing scope (up to 10). */
  const landingScopeDroppedPathsSampleRef = useRef<string[]>([]);

  function normalizePathForTarget(p: string): string {
    return String(p ?? "").trim().replace(/\\/g, "/").replace(/^\.\//, "").replace(/^\/+/, "");
  }

  function isLandingRun(): boolean {
    return Boolean(runContextRef.current?.simpleLandingIntent);
  }

  /** Allowed paths for simple landing: App, LandingPage, components/ui only. */
  function isAllowedLandingPath(norm: string): boolean {
    return (
      norm === "src/App.tsx" ||
      norm === "src/pages/LandingPage.tsx" ||
      norm.startsWith("src/components/ui/")
    );
  }

  // Builder event log for Debug HUD (key lifecycle steps with timestamps)
  type BuilderEvent = { ts: number; type: string; data?: unknown };
  const [builderEvents, setBuilderEvents] = useState<BuilderEvent[]>([]);
  const pushBuildEvent = useCallback((type: string, data?: unknown) => {
    setBuilderEvents((prev) => [...prev.slice(-49), { ts: Date.now(), type, data }]);
  }, []);
  const lastPipelineStatusRef = useRef<BuilderStatus | null>(null);
  const builderStatusRef = useRef(builderStatus);
  const buildReportRef = useRef(buildReport);
  const buildValidationErrorRef = useRef(buildValidationError);
  const buildValidationBucketRef = useRef(buildValidationBucket);
  type PreviewTelemetry = {
    sessionId: string;
    ts: number;
    rendered: boolean;
    bootStep?: string;
    lastError?: string;
    error?: string;
    stack?: string;
    url?: string;
  };
  const previewTelemetryRef = useRef<PreviewTelemetry | null>(null);
  const lastTelemetrySessionIdRef = useRef<string | null>(null);
  const renderAckRef = useRef<{
    sessionId: string;
    nonce: string;
    bootStep?: string;
    rendered: boolean;
    lastError?: string;
  } | null>(null);
  builderStatusRef.current = builderStatus;
  buildReportRef.current = buildReport;
  buildValidationErrorRef.current = buildValidationError;
  buildValidationBucketRef.current = buildValidationBucket;
  smokeTestStateRef.current = smokeTestState;
  const scaffoldReadyEmittedRef = useRef(false);
  const errorEmittedRef = useRef(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollAttemptRef = useRef(0);
  const pollActiveRef = useRef(false);

  const initialSnapshotCreatedRef = useRef<string | null>(null);

  // Sync builderStatus into the most recent build log item
  useEffect(() => {
    if (builderStatus === "idle" || builderStatus === "generating") return;
    setBuildLog((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      next[0] = {
        ...next[0],
        status: builderStatus,
        ...(builderStatus === "success" ? { summary: lastApplySummary ?? undefined } : {}),
        ...(builderStatus === "failed" ? { error: (buildValidationError ?? "").slice(0, 400) } : {}),
      };
      return next;
    });
  }, [builderStatus, lastApplySummary, buildValidationError]);

  // Emit PIPELINE_STATUS when builderStatus changes (dedupe); emit ERROR once when failed
  useEffect(() => {
    if (lastPipelineStatusRef.current !== builderStatus) {
      pushBuildEvent(`PIPELINE_STATUS:${builderStatus}`);
      lastPipelineStatusRef.current = builderStatus;
    }
    if (builderStatus === "failed") {
      if (!errorEmittedRef.current) {
        pushBuildEvent("ERROR", { error: (buildValidationError ?? "").slice(0, 200) });
        errorEmittedRef.current = true;
      }
    } else {
      errorEmittedRef.current = false;
    }
  }, [builderStatus, buildValidationError, pushBuildEvent]);

  // Emit FILES_LOADED when count changes; emit SCAFFOLD_READY once when framework is present
  useEffect(() => {
    pushBuildEvent("FILES_LOADED", loadedFileContents.size);
    if (hasDayOneFramework(loadedFileContents)) {
      if (!scaffoldReadyEmittedRef.current) {
        pushBuildEvent("SCAFFOLD_READY");
        scaffoldReadyEmittedRef.current = true;
      }
    }
  }, [loadedFileContents, pushBuildEvent]);

  // Reset scaffold-ready dedupe when project changes
  useEffect(() => {
    scaffoldReadyEmittedRef.current = false;
  }, [activeProject?.id]);

  useEffect(() => {
    if (user && currentProject && !snapshotsLoading && snapshots.length === 0 && hasDayOneFramework(loadedFileContents)) {
      if (initialSnapshotCreatedRef.current === currentProject.id) return;
      initialSnapshotCreatedRef.current = currentProject.id;
      const files = Array.from(loadedFileContents.entries()).map(([name, content]) => ({ name, content }));
      createSnapshot(currentProject.id, files, "Initial scaffold", "Initial scaffold");
    }
  }, [user, currentProject?.id, snapshotsLoading, snapshots.length, loadedFileContents, createSnapshot]);

  useEffect(() => {
    if (activeProject?.id !== initialSnapshotCreatedRef.current) {
      initialSnapshotCreatedRef.current = null;
    }
  }, [activeProject?.id]);

  useEffect(() => {
    const isDebug = searchParams.get("debug") === "1";
    const applyTelemetry = (data: { sessionId?: string; ts?: number; rendered?: boolean; bootStep?: string; lastError?: string; error?: string; stack?: string; url?: string }) => {
      if (!isDebug && data.sessionId !== sessionId) return;
      lastTelemetrySessionIdRef.current = data.sessionId ?? null;
      previewTelemetryRef.current = {
        sessionId: data.sessionId ?? "",
        ts: data.ts ?? Date.now(),
        rendered: Boolean(data.rendered),
        bootStep: data.bootStep,
        lastError: data.lastError,
        error: data.error,
        stack: data.stack,
        url: data.url,
      };
    };
    const msgHandler = (e: MessageEvent) => {
      const data = e.data;
      if (!data) return;
      if (data.type === "DAYONE_BUILD_META") {
        const stubs = Array.isArray(data.createdScreenStubs) ? data.createdScreenStubs : [];
        setCreatedScreenStubs(stubs);
        return;
      }
      if (data.type === "DAYONE_RENDER_ACK") {
        const fromPreview = e.source && iframeRef?.current?.contentWindow === e.source;
        if (fromPreview && data.sessionId != null && data.nonce != null) {
          renderAckRef.current = {
            sessionId: String(data.sessionId),
            nonce: String(data.nonce),
            bootStep: data.bootStep,
            rendered: Boolean(data.rendered),
            lastError: data.lastError,
          };
          lastTelemetrySessionIdRef.current = String(data.sessionId);
        }
        return;
      }
      if (data.type !== "DAYONE_PREVIEW_TELEMETRY") return;
      applyTelemetry(data);
    };
    const storageHandler = (e: StorageEvent) => {
      if (!e.key?.startsWith(TELEMETRY_KEY_PREFIX) || e.newValue == null) return;
      try {
        const parsed = JSON.parse(e.newValue) as { sessionId?: string; ts?: number; rendered?: boolean; bootStep?: string; lastError?: string; error?: string; stack?: string; url?: string };
        if (parsed && (parsed.sessionId != null || parsed.ts != null)) {
          if (isDebug) console.log("[CodeWorkspace] storage telemetry", parsed.sessionId, parsed.rendered, parsed.error);
          applyTelemetry(parsed);
        }
      } catch (_) {}
    };
    window.addEventListener("message", msgHandler);
    window.addEventListener("storage", storageHandler);
    return () => {
      window.removeEventListener("message", msgHandler);
      window.removeEventListener("storage", storageHandler);
    };
  }, [sessionId, searchParams]);

  useEffect(() => {
    if (user) listProjects();
  }, [user, listProjects]);

  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    setOnboardingDismissed(localStorage.getItem(onboardingStorageKey) === "true");
  }, [onboardingStorageKey]);

  // GitHub connection state is managed by useGitHubConnection hook (DB-backed)

  const handleGitHubConnectContinue = useCallback(async () => {
    const pat = githubConnectPatInput.trim();
    if (!pat) {
      toast.error("Enter a GitHub token");
      return;
    }
    try {
      const ok = await githubConnectWithPat(pat);
      if (ok) {
        setGitHubConnectModalOpen(false);
        setGitHubConnectPatInput("");
        toast.success("GitHub connected");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to connect GitHub");
    }
  }, [githubConnectPatInput, githubConnectWithPat]);

  const handleGitHubDisconnect = useCallback(async () => {
    await githubDisconnect();
    setGitHubConnectModalOpen(false);
    pushBuildEvent("GITHUB_DISCONNECT");
    toast.success("GitHub disconnected");
  }, [githubDisconnect, pushBuildEvent]);

  useEffect(() => {
    const loadProjectFromUrl = async () => {
      if (!projectIdFromUrl) return;

      if (!user && isLocalhost) {
        setDevProject({ id: projectIdFromUrl, name: "Dev Project" });
        return;
      }

      if (!user) return;
      if (currentProject?.id === projectIdFromUrl) return;
      if (projectLoadAttempted && projectLoadError) return;

      setProjectLoadAttempted(true);
      setProjectLoadError(null);

      const success = await selectProjectById(projectIdFromUrl);
      if (!success) setProjectLoadError("Project not found or you do not have access to it.");
    };

    loadProjectFromUrl();
  }, [
    projectIdFromUrl,
    user,
    isLocalhost,
    currentProject?.id,
    selectProjectById,
    projectLoadAttempted,
    projectLoadError,
  ]);

  const handleFileSelect = useCallback(
    async (filename: string) => {
      setSelectedFile(filename);
      const content = await loadFile(filename);
      if (content !== null) {
        setFileContent(content);
        setEditorContent(content);
        setLoadedFileContents((prev) => new Map(prev).set(filename, content));
      }
    },
    [loadFile]
  );

  const getProjectFiles = useCallback(() => {
    const fileMap = new Map<string, string>();

    // include preloaded contents from DB
    for (const [name, content] of loadedFileContents.entries()) fileMap.set(name, content);

    // include current editor buffer (unsaved changes)
    if (selectedFile) fileMap.set(selectedFile, editorContent);

    // NO FALLBACK INJECTION — preview only runs from committed DB files
    // If there are no files, the boot guard will prevent preview from running

    console.log(`[getProjectFiles] loadedFileContents.size=${loadedFileContents.size} totalFiles=${fileMap.size}`);

    return Array.from(fileMap.entries()).map(([name, content]) => ({ name, content }));
  }, [loadedFileContents, selectedFile, editorContent]);

  // ── Self-healing preview: auto-detect runtime errors and fix them ──
  const selfHealApplyRef = useRef<UseSelfHealingPreviewOptions["onApplyFixes"]>(null!);
  const selfHealFixRef = useRef<UseSelfHealingPreviewOptions["onSendFixInstruction"]>(null!);
  const { selfHealState, triggerManualFix, resetSelfHeal, checkForErrorsNow } = useSelfHealingPreview({
    projectId: activeProject?.id || null,
    getProjectFiles,
    consoleLogs: sandboxConsoleLogs,
    sandboxError,
    builderStatus,
    onApplyFixes: async (...args) => selfHealApplyRef.current(...args),
    onSendFixInstruction: async (...args) => selfHealFixRef.current!(...args),
    enabled: builderStatus === "idle" || builderStatus === "success",
  });

  const handleCreateRepoAndPush = useCallback(async () => {
    const repoName = (githubPushRepoName || `DayOne-${(activeProject?.name || "project").replace(/[^a-zA-Z0-9-_]/g, "-")}`).trim();
    if (!repoName) {
      toast.error("Enter a repo name");
      return;
    }
    pushBuildEvent("GITHUB_PUSH_STARTED", { repoName });
    const files = getProjectFiles();
    const result = await pushToGitHub(files, repoName, { isPrivate: githubPushPrivate });
    if (result?.success) {
      pushBuildEvent("GITHUB_PUSH_DONE", { url: result.repoUrl });
      toast.success("Pushed to GitHub");
    } else if (result?.error) {
      pushBuildEvent("GITHUB_PUSH_ERROR", { error: result.error });
      toast.error(friendlyErrorMessage(result.error));
    }
  }, [activeProject?.name, githubPushRepoName, githubPushPrivate, getProjectFiles, pushToGitHub, pushBuildEvent]);

  const handlePushToExisting = useCallback(async () => {
    const owner = (githubPushExistingOwner || "").trim();
    const repo = (githubPushExistingRepo || "").trim();
    if (!owner || !repo) {
      toast.error("Enter owner and repo");
      return;
    }
    pushBuildEvent("GITHUB_PUSH_STARTED", { owner, repo });
    const files = getProjectFiles();
    const result = await pushToGitHub(files, repo, { repoFullName: `${owner}/${repo}` });
    if (result?.success) {
      pushBuildEvent("GITHUB_PUSH_DONE", { url: result.repoUrl });
      toast.success("Pushed to GitHub");
    } else if (result?.error) {
      pushBuildEvent("GITHUB_PUSH_ERROR", { error: result.error });
      toast.error(friendlyErrorMessage(result.error));
    }
  }, [githubPushExistingOwner, githubPushExistingRepo, getProjectFiles, pushToGitHub, pushBuildEvent]);

  const runPreviewWithSession = useCallback(
    async (projectFiles: any) => {
      setCreatedScreenStubs([]);

      // STEP 4 DEBUG: Log files being written to preview
      const appFile = projectFiles.find((f: any) => f.name === "src/App.tsx");
      console.log("[runPreviewWithSession] PREVIEW LOADED:", {
        sessionId,
        fileCount: projectFiles.length,
        filePaths: projectFiles.map((f: any) => f.name).slice(0, 20),
        hasAppTsx: !!appFile,
        appTsxSnippet: appFile?.content?.slice(0, 150),
      });
      // Log each file being loaded
      for (const f of projectFiles.slice(0, 30)) {
        console.log(`PREVIEW LOADED: ${f.name}`);
      }

      const result = await (runPreview as any)(projectFiles, sessionId);
      // Force iframe reload after writing files
      setPreviewKey((k) => k + 1);
      return result;
    },
    [runPreview, sessionId]
  );

  const handleRefreshPreview = useCallback(() => {
    setPreviewKey((k) => {
      const next = k + 1;
      console.log("[preview] refresh clicked", next);
      return next;
    });
    runPreviewWithSession(getProjectFiles());
  }, [runPreviewWithSession, getProjectFiles]);

  // preload all files
  useEffect(() => {
    if (!activeProject) return;
    if (!files || files.length === 0) return;

    let cancelled = false;
    const lastFetchAt = Date.now();
    pushBuildEvent("FILES_SOURCE", { mode: "preload_start", fileCount: files.length, lastFetchAt });

    (async () => {
      console.log(`[FILE PRELOAD] starting for ${files.length} files...`);
      const next = new Map<string, string>();
      try {
        await Promise.all(
          files.map(async (file) => {
            const path = file.path ?? file.name;
            const content = await loadFile(path);
            if (content != null) next.set(path, content);
          })
        );
      } catch (err) {
        if (!cancelled) pushBuildEvent("FILES_SOURCE", { mode: "preload_done", lastFetchAt, error: String(err) });
        return;
      }

      if (cancelled) return;

      const paths = Array.from(next.keys()).slice(0, 15);
      console.log(`[FILE PRELOAD] loaded ${next.size} files:`, paths);
      pushBuildEvent("FILES_SOURCE", { mode: "preload_done", lastFetchAt, loadedCount: next.size });

      setLoadedFileContents((prev) => {
        const merged = new Map(prev);
        for (const [k, v] of next.entries()) merged.set(k, v);
        return merged;
      });
      setFilesLoadedFromDB(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [activeProject?.id, files, loadFile, pushBuildEvent]);

  // Boot preview: single source of truth
  // Runs ONCE per project after files are loaded from DB
  // HARD GUARD: Requires DayOne framework marker files before booting
  const bootedProjectRef = useRef<string | null>(null);

  // Track if we're waiting for scaffold commit (insufficient files from DB)
  const [waitingForScaffold, setWaitingForScaffold] = useState(false);

  useEffect(() => {
    if (!buildStartedThisSessionRef.current || !waitingForScaffold) return;
    const t = setTimeout(() => setScaffoldWaitTimedOut(true), SCAFFOLD_WAIT_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [waitingForScaffold]);

  // Retry fetching files if waiting for scaffold (handles storage indexing delay)
  useEffect(() => {
    if (!waitingForScaffold || !activeProject || loading) return;

    console.log(`[SCAFFOLD RETRY] Retrying file fetch in 2s...`);
    const timer = setTimeout(async () => {
      await selectProject(activeProject as any, { silent: true });
    }, 2000);

    return () => clearTimeout(timer);
  }, [waitingForScaffold, activeProject, loading, selectProject]);

  // Fallback polling: only when build is active (Send hit or waiting for scaffold) and files not yet at threshold
  const isBuildActive =
    builderStatus === "generating" ||
    builderStatus === "applying" ||
    builderStatus === "building" ||
    builderStatus === "fixing" ||
    builderStatus === "polishing";
  const shouldPoll =
    !!activeProject &&
    !hasDayOneFramework(loadedFileContents) &&
    (waitingForScaffold || isBuildActive);

  useEffect(() => {
    if (!shouldPoll) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        if (pollActiveRef.current) {
          pushBuildEvent("POLL_FILES_STOP", {
            reason: hasDayOneFramework(loadedFileContents) ? "scaffold_ready" : "builder_idle",
          });
          pollActiveRef.current = false;
        }
      }
      return;
    }

    if (pollIntervalRef.current) return;

    pollActiveRef.current = true;
    pollAttemptRef.current = 0;
    const POLL_INTERVAL_MS = 2000;
    const POLL_MAX_ATTEMPTS = 15;

    const tick = async () => {
      pollAttemptRef.current += 1;
      const attempt = pollAttemptRef.current;
      pushBuildEvent("POLL_FILES_TICK", { attempt });
      try {
        await selectProject(activeProject as any, { silent: true });
      } catch (_) {
        // ignore; next tick will retry
      }
      if (attempt >= POLL_MAX_ATTEMPTS) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        pushBuildEvent("POLL_FILES_STOP", { reason: "max_attempts" });
        pollActiveRef.current = false;
      }
    };

    tick();
    pollIntervalRef.current = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [shouldPoll, activeProject, loadedFileContents.size, waitingForScaffold, builderStatus, pushBuildEvent, selectProject]);

  useEffect(() => {
    const dbFileCount = files?.length || 0;
    const loadedCount = loadedFileContents.size;

    console.log(`[BOOT CHECK] activeProject=${activeProject?.id || 'null'} loading=${loading} dbFiles=${dbFileCount} loadedContents=${loadedCount} booted=${bootedProjectRef.current} builderStatus=${builderStatus}`);

    if (!activeProject) return;

    // SKIP: Don't boot during active build - handleApplyEdits will handle preview
    if (builderStatus === "generating" || builderStatus === "applying" || builderStatus === "building" || builderStatus === "fixing" || builderStatus === "polishing") {
      console.log(`[BOOT SKIP] build in progress (${builderStatus})`);
      return;
    }

    // SKIP: Already booted this project
    if (bootedProjectRef.current === activeProject.id) {
      setWaitingForScaffold(false);
      setScaffoldWaitTimedOut(false);
      return;
    }

    // WAIT: Don't boot while files are being fetched from DB
    if (loading) {
      console.log(`[BOOT WAIT] waiting for file list to load from DB...`);
      return;
    }

    // WAIT: If DB has files but content not loaded yet, wait for preload to finish
    if (dbFileCount > 0 && loadedCount === 0) {
      console.log(`[BOOT WAIT] waiting for ${dbFileCount} file contents to load...`);
      return;
    }

    // HARD GUARD: Require DayOne framework marker files. Only block with waitingForScaffold if a build was started this session (Build This Idea).
    if (!hasDayOneFramework(loadedFileContents)) {
      if (buildStartedThisSessionRef.current) {
        console.log("[BOOT BLOCKED] framework marker files missing");
        setWaitingForScaffold(true);
      }
      return;
    }

    // BOOT: Write files and trigger preview
    setWaitingForScaffold(false);
    setScaffoldWaitTimedOut(false);
    bootedProjectRef.current = activeProject.id;
    const projectFiles = getProjectFiles();
    const paths = projectFiles.map(f => f.name).slice(0, 15);

    console.log(`[BOOT PREVIEW] sessionId=${sessionId} fileCount=${projectFiles.length} loadedFromDB=${loadedCount} paths=`, paths);
    pushBuildEvent("PREVIEW_BOOT");
    setLastPreviewBootedAt(Date.now());
    runPreviewWithSession(projectFiles);
  }, [activeProject?.id, files?.length, loadedFileContents.size, loading, getProjectFiles, runPreviewWithSession, sessionId, pushBuildEvent, builderStatus]);

  // rerun when file hash changes
  const lastFilesHashRef = useRef<string>("");

  const computeFilesHash = useCallback(() => {
    const list = getProjectFiles();
    let h = 0;
    for (const f of list) {
      const s = (f.name || "") + ":" + (f.content || "");
      for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    }
    return String(h);
  }, [getProjectFiles]);

  useEffect(() => {
    if (!activeProject) return;
    // Don't run during build - handleApplyEdits will call runPreviewWithSession directly
    if (builderStatus === "generating" || builderStatus === "applying" || builderStatus === "building" || builderStatus === "fixing" || builderStatus === "polishing") {
      return;
    }

    const h = computeFilesHash();
    if (!lastFilesHashRef.current) {
      lastFilesHashRef.current = h;
      return;
    }
    if (h === lastFilesHashRef.current) return;

    lastFilesHashRef.current = h;

    if (previewUrl || sandboxRunning) {
      runPreviewWithSession(getProjectFiles());
    }
  }, [activeProject?.id, computeFilesHash, getProjectFiles, previewUrl, sandboxRunning, runPreviewWithSession, builderStatus]);

  const handleSave = useCallback(async () => {
    if (selectedFile && editorContent !== fileContent) {
      const success = await saveFile(selectedFile, editorContent);
      if (success) {
        setFileContent(editorContent);
        toast.success("File saved");
      }
    }
  }, [selectedFile, editorContent, fileContent, saveFile]);

  const handlePromptSubmit = async (prompt: string) => {
    setGenerationLogs(["Creating project...", "Analyzing your idea...", "Designing app architecture..."]);

    if (!user && !isLocalhost) {
      toast.error("Please sign in to build projects.");
      navigate("/signin");
      return;
    }

    // Dev mode AND authenticated users both use the same scaffold flow
    // createProjectFromPrompt requires the user to be signed in
    await createProjectFromPrompt(prompt);
  };

  const handleSendAIMessage = useCallback(
    async (message: string | { prompt: string; overrideSimpleLandingIntent?: boolean; fromRetry?: boolean }) => {
      const prompt = typeof message === "string" ? message : message.prompt;
      const overrideSimpleLandingIntent = typeof message === "object" && message && "overrideSimpleLandingIntent" in message ? message.overrideSimpleLandingIntent : undefined;
      const fromRetry = typeof message === "object" && message && "fromRetry" in message ? message.fromRetry : false;

      console.log("[handleSendAIMessage] CALLED", { prompt: prompt?.slice(0, 80), activeProjectId: activeProject?.id, builderStatus, fromRetry, chatMode });

      if (!activeProject?.id) {
        console.log("[handleSendAIMessage] BAIL: no activeProject.id");
        toast.error("No active project selected");
        return;
      }
      if (builderStatus === "generating") { console.log("[handleSendAIMessage] BAIL: already generating"); return; }

      // TODO: re-enable paywall gate before public launch
      // ── Paywall gate: free tier users cannot edit existing apps ──
      // BYPASSED for testing
      // if (!subscriptionLoading && tier === "free" && !isFirstBuild(loadedFileContents)) {
      //   console.log("[handleSendAIMessage] PAYWALL: free tier cannot edit existing apps");
      //   setPaywallReason("edit_denied");
      //   setPaywallOpen(true);
      //   return;
      // }

      // ── Chat mode: talk about the app without building ──
      if (chatMode && !fromRetry) {
        pushMessage({ role: "user", content: prompt, timestamp: new Date() });
        setChatLoading(true);
        try {
          const filePaths = Array.from(loadedFileContents.keys());
          const history = aiMessages.slice(-5).map(m => ({ role: m.role, content: m.content }));
          const data = await invokeEdgeFunction<{ message?: string }>("builder-chat-simple", {
            message: prompt,
            appName: activeProject.name || "your app",
            filePaths,
            history,
          });
          pushMessage({
            role: "assistant",
            content: data.message || "I'm not sure how to help with that.",
            timestamp: new Date(),
          });
        } catch (err) {
          console.error("[handleSendAIMessage] Chat mode error:", err);
          pushMessage({
            role: "assistant",
            content: "Sorry, I had trouble responding. Try again or switch to Build mode to make changes.",
            timestamp: new Date(),
          });
        } finally {
          setChatLoading(false);
        }
        return;
      }

      // ── API key collection: handle response to edit-time key prompt ──
      if (pendingKeyEditRef.current) {
        const pending = pendingKeyEditRef.current;
        const trimmed = prompt.trim().toLowerCase();

        pushMessage({ role: "user", content: prompt, timestamp: new Date() });

        if (trimmed === "skip") {
          pushMessage({
            role: "assistant",
            content: "Got it — keeping placeholder values for now. You can add keys later.",
            timestamp: new Date(),
          });
          pendingKeyEditRef.current = null;
          return;
        }

        // Parse key=value pairs or treat as single key
        const lines = prompt.trim().split(/\n/);
        if (pending.placeholders.length === 1 && lines.length === 1 && !lines[0].includes("=")) {
          collectedApiKeysRef.current[pending.placeholders[0].keyName] = lines[0].trim();
        } else {
          for (const line of lines) {
            const eqIdx = line.indexOf("=");
            if (eqIdx > 0) {
              const k = line.slice(0, eqIdx).trim();
              const v = line.slice(eqIdx + 1).trim();
              if (k && v) collectedApiKeysRef.current[k] = v;
            }
          }
        }

        // Build the edit instruction with real keys
        const keyEntries = Object.entries(collectedApiKeysRef.current).filter(([, v]) => !v.startsWith("YOUR_"));
        const keySection = keyEntries.map(([k, v]) => `${k}: ${v}`).join("\n");
        const augmentedPrompt = `${pending.prompt}\n\n[API KEYS — replace all placeholder values with these real keys in the code]\n${keySection}`;

        pendingKeyEditRef.current = null;
        pushMessage({
          role: "assistant",
          content: "Keys received — applying changes now.",
          timestamp: new Date(),
        });

        setTimeout(() => handleSendAIMessageRef.current?.({ prompt: augmentedPrompt, fromRetry: true }), 50);
        return;
      }

      // ── Edit-time key detection: if user asks to "make it real" and placeholders exist ──
      if (!fromRetry && isRealApiRequest(prompt)) {
        const currentFiles = getProjectFiles();
        const placeholders = scanFilesForPlaceholders(currentFiles);
        if (placeholders.length > 0) {
          const missing = placeholders.filter(p => !collectedApiKeysRef.current[p.keyName] || collectedApiKeysRef.current[p.keyName].startsWith("YOUR_"));
          if (missing.length > 0) {
            pushMessage({ role: "user", content: prompt, timestamp: new Date() });
            pendingKeyEditRef.current = { prompt, placeholders: missing };
            const lines = missing.map(p => `• **${p.service}** (\`${p.keyName}\`) — for ${p.reason}`);
            pushMessage({
              role: "assistant",
              content: `To make this work with real APIs, I need your keys:\n\n${lines.join("\n")}\n\nPaste ${missing.length === 1 ? "your key" : "keys one per line as `KEY_NAME=value`"} — or type **skip** to keep placeholders.`,
              timestamp: new Date(),
            });
            return;
          }
        }
      }

      // ── Vague fix guard: ask user to be specific before running a fix ──
      if (!fromRetry && hasDayOneFramework(loadedFileContents) && isVagueFixRequest(prompt)) {
        pushMessage({ role: "user", content: prompt, timestamp: new Date() });
        pushMessage({ role: "assistant", content: VAGUE_FIX_RESPONSE, timestamp: new Date() });
        pushBuildEvent("VAGUE_FIX_BLOCKED", { prompt: prompt.slice(0, 80) });
        return;
      }

      buildStartedThisSessionRef.current = true;
      if (!filesLoadedFromDB) {
        console.warn("[CodeWorkspace] Message sent before files fully loaded from DB");
      }
      setLastPrompt(prompt);
      setBuilderStatus("generating");
      pushBuildEvent("BUILD_STARTED", { prompt: prompt?.slice(0, 80) });
      setBuildLog((prev) =>
        [{ id: crypto.randomUUID(), ts: Date.now(), status: "generating", prompt }, ...prev].slice(0, 5)
      );

      let sentPrompt = prompt;
      let planSummaryForTargets: string | undefined;
      let planEnforcedForVague = false;
      let targetPathsForRun: string[] = [];
      let simpleLandingIntent = false;
      const runId = crypto.randomUUID();
      const startedAt = Date.now();

      // Only expand vague prompts on FIRST BUILD — edits should use user's message verbatim
      if (isFirstBuild(loadedFileContents) && isVaguePrompt(prompt)) {
        try {
          const data = await invokeEdgeFunction<{ plan?: Parameters<typeof planToSummary>[0] }>("generate-plan", {
            prompt,
            ideaContext: prompt,
          });
          const plan = data?.plan as Parameters<typeof planToSummary>[0] | undefined;
          if (plan && typeof plan === "object") {
            const planSummary = planToSummary(plan).slice(0, PLAN_SUMMARY_MAX_CHARS);
            if (planSummary) {
              const stored = {
                plan: {
                  problem: typeof plan.problem === "string" ? plan.problem : "",
                  icp: typeof plan.icp === "string" ? plan.icp : "",
                  mvpFeatures: Array.isArray(plan.mvpFeatures) ? plan.mvpFeatures.filter((x): x is string => typeof x === "string") : [],
                  monetization: typeof plan.monetization === "string" ? plan.monetization : "",
                  keyScreens: Array.isArray(plan.keyScreens) ? plan.keyScreens.filter((x): x is string => typeof x === "string") : [],
                  dataModel: Array.isArray(plan.dataModel) ? plan.dataModel.filter((x): x is string => typeof x === "string") : [],
                  fileTree: Array.isArray(plan.fileTree) ? plan.fileTree.filter((x): x is string => typeof x === "string") : [],
                },
                generatedAt: Date.now(),
              };
              localStorage.setItem(PLAN_STORAGE_KEY(activeProject.id), JSON.stringify(stored));
              planSummaryForTargets = planSummary;
              sentPrompt = `Build this application according to the following structured blueprint:\n${planSummary}`;
              planEnforcedForVague = true;
              pushBuildEvent("PLAN_ENFORCED_FOR_VAGUE_PROMPT");
            }
          }
        } catch (_) {
          // continue with original message if generate-plan fails
        }
      }

      // Only append cached blueprint on FIRST builds - edits use the user's message verbatim
      if (!planEnforcedForVague && isFirstBuild(loadedFileContents)) {
        try {
          const raw = localStorage.getItem(PLAN_STORAGE_KEY(activeProject.id));
          if (raw) {
            const parsed = JSON.parse(raw) as { plan?: Parameters<typeof planToSummary>[0]; generatedAt?: number };
            if (parsed?.plan && typeof parsed.plan === "object") {
              const planSummary = planToSummary(parsed.plan).slice(0, PLAN_SUMMARY_MAX_CHARS);
              if (planSummary) {
                planSummaryForTargets = planSummary;
                sentPrompt = `${prompt}\n\n---\nStartup Blueprint:\n${planSummary}`;
                pushBuildEvent("PLAN_USED_ON_BUILD");
              }
            }
          }
        } catch (_) {
          // ignore parse errors
        }
      }

      shellPathsThisRunRef.current = [];
      const hasFramework = hasDayOneFramework(loadedFileContents);
      if (!hasFramework) {
        const appName = activeProject?.name ?? "App";
        const frameworkFiles = getDayOneAppFrameworkScaffold(appName);
        const toWrite = frameworkFiles.filter((f) => !loadedFileContents.has(f.path));
        if (toWrite.length > 0) {
          const frameworkEdits = toWrite.map((f) => ({ filePath: f.path, action: "create" as const, content: f.content }));
          const result = await applyEdits(frameworkEdits);
          if (result.success) {
            shellPathsThisRunRef.current = toWrite.map((f) => f.path);
            setLoadedFileContents((prev) => {
              const next = new Map(prev);
              for (const f of toWrite) next.set(f.path, f.content);
              return next;
            });
            pushBuildEvent("FRAMEWORK_SCAFFOLD_WRITTEN", { count: toWrite.length });
          }
        }
      }

      const projectFiles = getProjectFiles();
      const existingPaths = projectFiles.map((f) => f.name);
      try {
        let targetsData: { targets?: Array<{ path?: string; action?: string; rationale?: string }> } | null = null;
        try {
          targetsData = await invokeEdgeFunction("generate-file-targets", {
            prompt: sentPrompt,
            planSummary: planSummaryForTargets,
            existingFiles: existingPaths,
          });
        } catch (_) {
          // continue without targets
        }
        if (targetsData?.targets && Array.isArray(targetsData.targets) && targetsData.targets.length > 0) {
          const targets = targetsData.targets as Array<{ path?: string; action?: string; rationale?: string }>;
          const valid = targets.filter((t) => t.path).map((t) => ({ path: t.path!, action: t.action ?? "edit", rationale: t.rationale }));
          if (overrideSimpleLandingIntent !== undefined) {
            simpleLandingIntent = overrideSimpleLandingIntent;
          } else if (isFirstBuild(loadedFileContents)) {
            // First build: disable landing filter. Agentic pipeline generates complete apps
            // with all dependencies (index.css, components, types). Filtering would break them.
            simpleLandingIntent = false;
            console.log("[handleSendAIMessage] First build detected — landing filter disabled");
          } else {
            const intentText = sentPrompt.split("---")[0]?.trim() ?? sentPrompt;
            const noComplexIntent = !/auth|login|signup|dashboard|routing|api|database|stripe|payments|supabase/i.test(intentText);
            simpleLandingIntent = noComplexIntent && /landing|hero|marketing\s*page|home\s*page|front\s*page|single\s*page/i.test(intentText);
          }

          if (simpleLandingIntent) {
            // HARD CLAMP: ignore model targets; use only allowed landing scope
            const landingExactPaths = [
              normalizePathForTarget("src/App.tsx"),
              normalizePathForTarget("src/pages/LandingPage.tsx"),
            ];
            allowedTargetPathsRef.current = new Set(landingExactPaths);
            allowedPrefixesRef.current = ["src/components/ui/"];
            const landingUiSuggestedTargets = [
              "src/App.tsx",
              "src/pages/LandingPage.tsx",
              "src/components/ui/Hero.tsx",
              "src/components/ui/Features.tsx",
              "src/components/ui/Testimonials.tsx",
              "src/components/ui/Pricing.tsx",
              "src/components/ui/Footer.tsx",
              "src/components/ui/Button.tsx",
              "src/components/ui/Input.tsx",
            ];
            targetPathsForRun = landingUiSuggestedTargets.filter(
              (p) => p !== "index.html" && p !== "src/main.tsx"
            );
            setLastFileTargets(
              landingUiSuggestedTargets
                .filter((p) => p !== "index.html" && p !== "src/main.tsx")
                .map((path) => ({ path, action: "edit" as const, rationale: "recommended for landing page" }))
            );
            const targetsBlock = targetPathsForRun
              .map((path) => `- ${path} (edit): recommended for landing page`)
              .join("\n");
            sentPrompt = `${sentPrompt}\n\n---\nFile Targets (must follow):\n${targetsBlock}`;
          } else {
            targetPathsForRun = valid
              .map((t) => t.path)
              .filter((p) => p !== "index.html" && p !== "src/main.tsx");
            setLastFileTargets(
              valid.filter((t) => t.path !== "index.html" && t.path !== "src/main.tsx")
            );
            allowedTargetPathsRef.current = new Set(valid.map((t) => normalizePathExtension(normalizePathForTarget(t.path))));
            if (hasDayOneFramework(loadedFileContents)) {
              // Always allow framework entry + common layout files through scope check
              for (const frameworkPath of [
                "src/App.tsx",
                "src/components/layout/Sidebar.tsx",
                "src/components/layout/Header.tsx",
                "src/components/layout/Layout.tsx",
              ]) {
                allowedTargetPathsRef.current.add(normalizePathForTarget(frameworkPath));
              }
            }
            if (allowedTargetPathsRef.current.size <= 30) {
              allowedPrefixesRef.current = null;
            } else {
              allowedPrefixesRef.current = Array.isArray(targetsData.allowedPrefixes)
                ? (targetsData.allowedPrefixes as string[]).filter((x): x is string => typeof x === "string")
                : null;
            }
            const targetsBlock = targets
              .filter((t) => t.path && t.path !== "index.html" && t.path !== "src/main.tsx")
              .map((t) => `- ${t.path} (${t.action ?? "edit"}): ${(t.rationale ?? "").trim() || "update"}`)
              .join("\n");
            sentPrompt = `${sentPrompt}\n\n---\nFile Targets (must follow):\n${targetsBlock}`;
          }

          pushBuildEvent("FILE_TARGETS_READY", { count: targetPathsForRun.length });
          const allowed = allowedTargetPathsRef.current;
          const prefixes = allowedPrefixesRef.current;
          if (allowed?.size || (prefixes?.length ?? 0)) {
            const fromAllowed = (allowed && allowed.size <= 30) ? [...allowed].slice(0, 30) : [];
            const fromPrefixes = prefixes?.length ? prefixes : [];
            const list = [...fromAllowed, ...fromPrefixes].join(", ");
            if (list) sentPrompt += `\n\nYou may ONLY modify files within these allowed paths/prefixes:\n${list}\n\nDo not create or reference files outside allowed scope. No hallucinated paths.`;
            if (simpleLandingIntent) {
              sentPrompt +=
                "\n\nLANDING PAGE RULES (strict):" +
                "\n- For landing pages, ONLY create components under src/components/ui/." +
                "\n- Do NOT create src/components/HeroSection.tsx, FeaturesSection.tsx, Footer.tsx, etc." +
                "\n- Import with explicit relative .tsx paths." +
                "\n- Do NOT edit index.html or src/main.tsx." +
                "\n- Do NOT create .js/.jsx. Use .tsx/.ts.";
            }
          }
          if (lastGuardFailureRef.current?.errorCode === "PATCH_GUARD_INVALID_EXTENSION")
            sentPrompt += "\n\nIf any target path ends with .js or .jsx, create/modify the same path using .tsx instead.";
          if (simpleLandingIntent)
            sentPrompt +=
              "\n\nUse explicit .tsx import paths that exist. " +
              "Do NOT import extensionless local modules. Do NOT use absolute-style imports like from 'src/...'. " +
              "CRITICAL: If you import a local component file under src/components/ui/, you MUST create or update that file in the SAME run BEFORE importing it. Do not import files that do not exist.";
        } else {
          allowedTargetPathsRef.current = null;
          allowedPrefixesRef.current = null;
        }
      } catch (_) {
        allowedTargetPathsRef.current = null;
        allowedPrefixesRef.current = null;
      }

      sentPrompt = `${sentPrompt}\n\n---\nFramework rules (DayOne App Framework):\n- CRITICAL: Use the EXACT file paths from the approved plan. The plan is the source of truth for all paths.\n- Add reusable UI in src/components/ui/.\n- Add layout components only under src/components/layout/.\n- Add business logic under src/services/ or src/hooks/.\n- Update routing only in src/App.tsx (Routes/Route).\n- Update nav only in src/components/layout/Sidebar.tsx.\n- Do NOT edit index.html or src/main.tsx.`;

      sentPrompt += "\n\nCRITICAL SANDBOX RULES:\n" +
        "- NEVER import from https://esm.sh or any external CDN.\n" +
        "- NEVER use full URL imports.\n" +
        "- Always use local React imports like: import React from 'react';\n" +
        "- Always use: import { createRoot } from 'react-dom/client';\n" +
        "- Do NOT reference esm.sh in any file.\n";

      runContextRef.current = {
        runId,
        startedAt,
        userPromptSnippet: prompt.slice(0, 80),
        userPrompt: prompt,
        usedPlan: !!planSummaryForTargets,
        targetPaths: targetPathsForRun,
        simpleLandingIntent,
      };
      if (!fromRetry) lastUserInitiatedRunIdRef.current = runId;

      landingFilterDroppedRef.current = false;
      landingFilterDroppedCountRef.current = 0;
      landingScopeDroppedCountRef.current = 0;
      landingScopeDroppedPathsSampleRef.current = [];
      try {
        const plan = await sendInstruction(
          prompt,
          activeProject.id,
          projectFiles,
          projectGraph,
          "build",
          undefined,
          {
            filterForLanding: simpleLandingIntent,
            onLandingFilterDropped: (n) => {
              landingFilterDroppedRef.current = true;
              landingFilterDroppedCountRef.current = n;
            },
            // Send expanded blueprint text to edge function only; original prompt stored in history
            ...(sentPrompt !== prompt && { overrideMessage: sentPrompt }),
          }
        );
        // DEBUG: Log plan after sendInstruction to trace BUG 2
        console.log("[handleSendAIMessage] sendInstruction returned:", {
          hasPlan: !!plan,
          editsLength: plan?.edits?.length ?? 0,
          hasHandleApplyEdits: !!handleApplyEditsRef.current,
          summary: plan?.summary?.slice(0, 100),
        });
        if (plan?.edits?.length && handleApplyEditsRef.current) {
          console.log("[handleSendAIMessage] Calling handleApplyEdits with", plan.edits.length, "edits");
          await handleApplyEditsRef.current(plan.edits, { simpleLandingIntent });
          // Dismiss the plan from the chat message so "Apply changes" buttons don't linger
          dismissLastPlan();
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setBuilderStatus("failed");
        setBuildValidationError(friendlyErrorMessage(msg));
        setBuildValidationBucket("EDGE_FUNCTION_APPLY");
        const ctx = runContextRef.current;
        if (ctx) {
          setBuildReport({
            runId: ctx.runId,
            status: "failed",
            startedAt: ctx.startedAt,
            finishedAt: Date.now(),
            userPromptSnippet: ctx.userPromptSnippet,
            usedPlan: ctx.usedPlan,
            targetPaths: ctx.targetPaths,
            changedPaths: [],
            errorBucket: "EDGE_FUNCTION_APPLY",
            lastErrorCode: null,
            landingScopeDroppedCount: landingFilterDroppedCountRef.current,
            landingScopeDroppedPathsSample: [...landingScopeDroppedPathsSampleRef.current],
          });
          pushBuildEvent("BUILD_REPORT_READY");
        }
        throw err;
      } finally {
        setBuilderStatus((prev) => (prev === "generating" ? "idle" : prev));
      }
    },
    [activeProject, builderStatus, getProjectFiles, sendInstruction, projectGraph, pushBuildEvent, loadedFileContents.size, applyEdits, pushMessage, chatMode]
  );
  handleSendAIMessageRef.current = handleSendAIMessage;

  const handleApplyEdits = async (
    edits: any[],
    options?: { isFixAttempt?: boolean; skipFailToast?: boolean; simpleLandingIntent?: boolean; isPreflightRetry?: boolean; _autoFixDepth?: number }
  ): Promise<{ buildSuccess: boolean; buildError?: string; applyResult?: any; updatedFiles?: { name: string; content: string }[]; errorCode?: string; maxPatchItems?: number }> => {
    // DEBUG: Log entry to handleApplyEdits to trace BUG 2
    console.log("[handleApplyEdits] ENTERED with", edits.length, "edits", {
      editPaths: edits.slice(0, 10).map((e: any) => e.filePath || e.path),
      options,
    });
    const isFixAttempt = options?.isFixAttempt === true;
    const autoFixDepth = options?._autoFixDepth ?? 0;
    const skipFailToast = options?.skipFailToast === true;
    const isPreflightRetry = options?.isPreflightRetry === true;
    const simpleLanding = options?.simpleLandingIntent ?? isLandingRun();
    // Compute early for use in guard calls (bypass MAX_PATCH_ITEMS on initial builds)
    const isInitialBuildFlag = isFirstBuild(loadedFileContents);
    if (!isFixAttempt) setFixAttempted(false);

    // Check daily build limit (free tier)
    if (!isFixAttempt && !canBuild(getDailyBuildCount())) {
      toast.error("You've reached your daily build limit. Upgrade to Builder for unlimited builds.");
      return { buildSuccess: false, buildError: "Daily build limit reached" };
    }

    const raw = editsToRaw(edits);
    const validation = validateBuilderPatch(raw);
    setLastPatchValidation({
      rejectedPatchItems: validation.rejected,
      acceptedCount: validation.acceptedCount,
      rejectedCount: validation.rejectedCount,
      patchTotalChars: validation.totalChars,
    });
    const sanitizedEdits = patchItemsToEdits(validation.patch);
    if (edits.length > 0 && sanitizedEdits.length === 0) {
      const msg = validation.rejected.length > 0
        ? `All ${validation.rejectedCount} patch item(s) rejected. First: ${validation.rejected[0]?.reason ?? "invalid"}`
        : "No valid patch items after validation.";
      lastGuardFailureRef.current = { errorCode: "PATCH_VALIDATION", maxPatchItems: undefined };
      setBuilderStatus("failed");
      setBuildValidationError(friendlyErrorMessage(msg));
      setBuildValidationBucket("PATCH_GUARD");
      if (!skipFailToast) toast.error(friendlyErrorMessage(msg));
      return { success: false, buildSuccess: false, buildError: friendlyErrorMessage(msg), errorCode: "PATCH_VALIDATION" };
    }
    let editsToGuard = sanitizedEdits.length > 0 ? sanitizedEdits : edits;
    if (simpleLanding) {
      const { filteredEdits, droppedEdits } = filterEditsForLanding(edits);
      editsToGuard = filteredEdits;
      if (droppedEdits.length > 0) {
        const sample = droppedEdits.slice(0, 5).map((d) => d.path).join(", ");
        console.warn(`[landing-filter] dropped ${droppedEdits.length} out-of-scope edits: ${sample}`);
        landingFilterDroppedRef.current = true;
        landingFilterDroppedCountRef.current = droppedEdits.length;
      }
    }

    let guardedEdits: typeof editsToGuard = [];
    let guardError: string | undefined;
    let guardErrorCode: string | undefined;
    let guardMaxPatchItems: number | undefined;
    let editsAfterGuard = guardAndCoalescePatches(editsToGuard, MAX_PATCH_ITEMS, { isInitialBuild: isInitialBuildFlag });

    if (editsAfterGuard.error && editsAfterGuard.errorCode === "PATCH_GUARD_PROTECTED_FILE") {
      const protectedPaths = new Set(["index.html", "src/main.tsx"]);
      const filtered = editsToGuard.filter((e) => {
        const raw = e?.filePath ?? e?.path;
        if (raw == null) return false;
        const norm = normalizePathForTarget(raw);
        return norm && !protectedPaths.has(norm);
      });
      if (filtered.length > 0) {
        editsAfterGuard = guardAndCoalescePatches(filtered, MAX_PATCH_ITEMS, { isInitialBuild: isInitialBuildFlag });
        if (!editsAfterGuard.error) {
          lastGuardFailureRef.current = { errorCode: "PATCH_GUARD_PROTECTED_FILE", maxPatchItems: editsAfterGuard.maxPatchItems };
          pushBuildEvent("PATCH_GUARD_PROTECTED_FILE_AUTO_RETRY", { dropped: editsToGuard.length - filtered.length });
        }
      }
    }

    guardedEdits = editsAfterGuard.edits;
    guardError = editsAfterGuard.error;
    guardErrorCode = editsAfterGuard.errorCode;
    guardMaxPatchItems = editsAfterGuard.maxPatchItems;

    if (guardError) {
      lastGuardFailureRef.current = { errorCode: guardErrorCode, maxPatchItems: guardMaxPatchItems };
      setBuilderStatus("failed");
      setBuildValidationError(guardError ? friendlyErrorMessage(guardError) : null);
      setBuildValidationBucket("PATCH_GUARD");
      const ctx = runContextRef.current;
      if (ctx) {
        setBuildReport({
          runId: ctx.runId,
          status: "failed",
          startedAt: ctx.startedAt,
          finishedAt: Date.now(),
          userPromptSnippet: ctx.userPromptSnippet,
          usedPlan: ctx.usedPlan,
          targetPaths: ctx.targetPaths,
          changedPaths: [],
          errorBucket: `PATCH_GUARD_${guardErrorCode ?? "BLOCKED"}`,
          lastErrorCode: guardErrorCode ?? null,
          landingScopeDroppedCount: landingScopeDroppedCountRef.current,
          landingScopeDroppedPathsSample: [...landingScopeDroppedPathsSampleRef.current],
        });
        pushBuildEvent("BUILD_REPORT_READY");
      }
      if (!skipFailToast) toast.error(friendlyErrorMessage(guardError));
      return { success: false, buildSuccess: false, buildError: friendlyErrorMessage(guardError), errorCode: guardErrorCode, maxPatchItems: guardMaxPatchItems };
    }
    edits = guardedEdits;

    const allowed = allowedTargetPathsRef.current;
    const allowedPrefixes = allowedPrefixesRef.current;
    const userPrompt = runContextRef.current?.userPrompt ?? "";
    const userExplicitlyRequestedEntrypoints = /(\bindex\.html\b|\bsrc\/main\.tsx\b|\bmain\.tsx\b)/i.test(userPrompt);

    // Any file under src/ or .dayone/ is always allowed regardless of plan scope.
    // The server-side scope filter and plan prompt handle restricting to valid paths;
    // the client guard should never block a legitimate src/ file.
    const ALWAYS_ALLOWED_PREFIXES = [
      "src/",
      ".dayone/",
      "supabase/",
    ];
    // Root config files the AI shouldn't edit, but if it does, don't crash the build
    const ALWAYS_ALLOWED_ROOT_FILES = new Set([
      "package.json",
      "vite.config.ts",
      "vite.config.js",
      "tsconfig.json",
      "tailwind.config.js",
      "tailwind.config.ts",
      "postcss.config.js",
      "postcss.config.cjs",
      "src/index.css",
    ]);

    if (allowed && allowed.size > 0) {
      // Rewrite patch paths that differ only by case from targetPaths (e.g. src/app.tsx -> src/App.tsx)
      edits = rewritePatchPathsToMatchTargets(edits, Array.from(allowed));
      // Build lowercase lookup for case-insensitive scope matching
      const allowedLower = new Set(Array.from(allowed).map((p) => p.toLowerCase()));
      const outOfScope: string[] = [];
      for (const edit of edits) {
        const path = edit?.filePath ?? edit?.path;
        if (path == null) continue;
        const norm = normalizePathForTarget(path);
        if (!norm) continue;
        if ((norm === "index.html" || norm === "src/main.tsx") && !userExplicitlyRequestedEntrypoints) {
          outOfScope.push(path);
          continue;
        }
        const inTargets = allowed.has(norm) || allowedLower.has(norm.toLowerCase());
        const inPrefixes = allowedPrefixes?.length
          ? allowedPrefixes.some((prefix) => norm.toLowerCase().startsWith(prefix.toLowerCase()))
          : false;
        const inAlwaysAllowed = ALWAYS_ALLOWED_PREFIXES.some((prefix) => norm.toLowerCase().startsWith(prefix));
        const inRootAllowed = ALWAYS_ALLOWED_ROOT_FILES.has(norm);
        if (!inTargets && !inAlwaysAllowed && !inRootAllowed && (allowedPrefixesRef.current == null || !inPrefixes)) outOfScope.push(path);
      }
      if (outOfScope.length > 0) {
        const onlyEntrypointsBlocked = outOfScope.every((p) => {
          const n = normalizePathForTarget(p);
          return n === "index.html" || n === "src/main.tsx";
        });
        if (onlyEntrypointsBlocked && userPrompt && handleSendAIMessageRef.current) {
          const constraint =
            "\n\nDo NOT edit index.html or src/main.tsx. Only modify files under src/ (App.tsx for routes, Sidebar.tsx for nav).";
          allowedTargetPathsRef.current = null;
          allowedPrefixesRef.current = null;
          setBuildValidationError(null);
          setBuildValidationBucket(null);
          handleSendAIMessageRef.current({
            prompt: userPrompt + constraint,
            overrideSimpleLandingIntent: runContextRef.current?.simpleLandingIntent,
          });
          pushBuildEvent("OUT_OF_SCOPE_EDIT_ENTRYPOINT_AUTO_RETRY", { paths: outOfScope });
          return { success: false, buildSuccess: false, buildError: "OUT_OF_SCOPE_EDIT", errorCode: "PATCH_GUARD_OUT_OF_SCOPE" };
        }
        console.warn("[CodeWorkspace] OUT_OF_SCOPE_EDIT blocked paths:", outOfScope);
        allowedTargetPathsRef.current = null;
        allowedPrefixesRef.current = null;
        setBuilderStatus("failed");
        setBuildValidationError("AI tried to edit files outside the allowed targets. Retrying…");
        setBuildValidationBucket("OUT_OF_SCOPE_EDIT");
        pushBuildEvent("OUT_OF_SCOPE_EDIT", { paths: outOfScope });
        const ctx = runContextRef.current;
        if (ctx) {
          setBuildReport({
            runId: ctx.runId,
            status: "failed",
            startedAt: ctx.startedAt,
            finishedAt: Date.now(),
            userPromptSnippet: ctx.userPromptSnippet,
            usedPlan: ctx.usedPlan,
            targetPaths: ctx.targetPaths,
            changedPaths: [],
            errorBucket: "OUT_OF_SCOPE_EDIT",
            lastErrorCode: "PATCH_GUARD_OUT_OF_SCOPE",
            outOfScopePaths: outOfScope,
            landingScopeDroppedCount: landingScopeDroppedCountRef.current,
            landingScopeDroppedPathsSample: [...landingScopeDroppedPathsSampleRef.current],
          });
          pushBuildEvent("BUILD_REPORT_READY");
        }
        shellPathsThisRunRef.current = [];
        if (!skipFailToast) {
          toast.error("Edits were outside the file target list. Use \"Retry within targets\".");
        }
        return { success: false, buildSuccess: false, buildError: "OUT_OF_SCOPE_EDIT", errorCode: "PATCH_GUARD_OUT_OF_SCOPE" };
      }
    }

    // FINAL scope gate for simple landing: defense-in-depth before apply
    if (simpleLanding) {
      const inScope: any[] = [];
      const dropped: string[] = [];
      for (const edit of edits) {
        const raw = edit?.filePath ?? edit?.path;
        if (raw == null) continue;
        const norm = normalizePathForTarget(raw);
        if (!norm || norm.includes("..")) continue;
        if (isAllowedLandingPath(norm)) {
          inScope.push(edit);
        } else {
          dropped.push(raw);
        }
      }
      if (dropped.length > 0) {
        const sample = dropped.slice(0, 10);
        console.warn(
          `[landing-scope] FINAL gate dropped ${dropped.length} out-of-scope edits:`,
          sample.join(", ")
        );
        landingScopeDroppedCountRef.current = dropped.length;
        landingScopeDroppedPathsSampleRef.current = sample;
      }
      if (inScope.length === 0) {
        const ctx = runContextRef.current;
        if (ctx) {
          setBuildReport({
            runId: ctx.runId,
            status: "failed",
            startedAt: ctx.startedAt,
            finishedAt: Date.now(),
            userPromptSnippet: ctx.userPromptSnippet,
            usedPlan: ctx.usedPlan,
            targetPaths: ctx.targetPaths,
            changedPaths: [],
            errorBucket: "LANDING_SCOPE_DROPPED_ALL",
            lastErrorCode: "LANDING_SCOPE_DROPPED_ALL",
            landingScopeDroppedCount: dropped.length,
            landingScopeDroppedPathsSample: dropped.slice(0, 10),
          });
          pushBuildEvent("BUILD_REPORT_READY");
        }
        setBuilderStatus("failed");
        setBuildValidationError(
          "The AI tried to edit files outside the landing scope. Only src/App.tsx, src/pages/LandingPage.tsx, and src/components/ui/* are allowed."
        );
        setBuildValidationBucket("LANDING_SCOPE_DROPPED_ALL");
        if (!skipFailToast) {
          toast.error("Edits were outside the landing scope. Retry with a simpler prompt.");
        }
        return {
          success: false,
          buildSuccess: false,
          buildError: "LANDING_SCOPE_DROPPED_ALL",
          errorCode: "LANDING_SCOPE_DROPPED_ALL",
        };
      }
      edits = inScope;
    }

    // Preflight DISABLED: client-side sandbox build has safety nets (sanitizeFileMap,
    // ensureDefaultExports, fixDuplicateExports) that fix most AI-generated issues.
    // The preflight was rejecting patches that would work fine after those fixes run.
    const preflightMeta: { ok: boolean; errorCount?: number; firstErrors?: string[]; attemptedRetry?: boolean; retryOutcome?: "succeeded" | "failed" | "no_edits" } = { ok: true };
    setLastPreflightResult(preflightMeta);
    console.log("[preflight] SKIPPED — relying on client-side sandbox safety nets");

    setBuilderStatus("applying");
    setBuildValidationError(null);
    setBuildValidationBucket(null);
    try {
      // Apply call: persisted via code-workspace-files applyEdits.
      // Pass isInitialBuild to bypass deny-prefix on first builds (scaffold files)
      // (isInitialBuildFlag computed at top of handleApplyEdits)
      const result = await applyEdits(edits, { isInitialBuild: isInitialBuildFlag });
      if (!result.success) {
        lastGuardFailureRef.current = { errorCode: result.errorCode, maxPatchItems: result.maxPatchItems };
        setBuilderStatus("failed");
        setBuildValidationError(result.summary ? friendlyErrorMessage(result.summary) : null);
        const ctxApply = runContextRef.current;
        if (ctxApply) {
          setBuildReport({
            runId: ctxApply.runId,
            status: "failed",
            startedAt: ctxApply.startedAt,
            finishedAt: Date.now(),
            userPromptSnippet: ctxApply.userPromptSnippet,
            usedPlan: ctxApply.usedPlan,
            targetPaths: ctxApply.targetPaths,
            changedPaths: [],
            errorBucket: "PATCH_GUARD",
            lastErrorCode: result.errorCode ?? null,
            landingScopeDroppedCount: landingScopeDroppedCountRef.current,
            landingScopeDroppedPathsSample: [...landingScopeDroppedPathsSampleRef.current],
          });
          pushBuildEvent("BUILD_REPORT_READY");
        }
        if (!skipFailToast) toast.error(friendlyErrorMessage(result.summary));
        return { success: false, buildSuccess: false, buildError: friendlyErrorMessage(result.summary), errorCode: result.errorCode, maxPatchItems: result.maxPatchItems };
      }

      // Build updated contents eagerly (React state hasn't committed yet)
      const nextContents = new Map(loadedFileContents);

      // DEBUG: Log what we're starting with and what edits we're applying
      const existingAppTsx = nextContents.get("src/App.tsx");
      console.log("[handleApplyEdits] BEFORE edits:", {
        existingKeys: Array.from(nextContents.keys()).filter(k => k.toLowerCase().includes("app")),
        hasAppTsx: !!existingAppTsx,
        existingAppTsxSnippet: existingAppTsx?.slice(0, 100),
        editCount: edits.length,
        editPaths: edits.map(e => e.filePath),
        editPathsWithContent: edits.filter(e => e.content).map(e => ({ path: e.filePath, contentLen: e.content?.length })),
      });

      for (const edit of edits) {
        if (edit.action !== "delete" && edit.content) {
          // DEBUG: Log each App.tsx edit
          if (edit.filePath.toLowerCase().includes("app")) {
            console.log("[handleApplyEdits] Applying App edit:", {
              path: edit.filePath,
              action: edit.action,
              contentSnippet: edit.content?.slice(0, 200),
            });
          }
          nextContents.set(edit.filePath, edit.content!);
        } else if (edit.action === "delete") {
          nextContents.delete(edit.filePath);
        }
      }

      // DEBUG: Log what we have after edits
      const afterAppTsx = nextContents.get("src/App.tsx");
      console.log("[handleApplyEdits] AFTER edits:", {
        keys: Array.from(nextContents.keys()).filter(k => k.toLowerCase().includes("app")),
        hasAppTsx: !!afterAppTsx,
        afterAppTsxSnippet: afterAppTsx?.slice(0, 100),
      });

      // Inject missing framework files so hasDayOneFramework() passes and preview boots
      // stream-app-build tells AI not to generate these (they're "scaffold"), but they must exist
      if (!nextContents.has("src/main.tsx") || !nextContents.has("src/lib/utils.ts")) {
        const appName = activeProject?.name ?? "App";
        const frameworkFiles = getDayOneAppFrameworkScaffold(appName);
        for (const f of frameworkFiles) {
          if (!nextContents.has(f.path)) {
            nextContents.set(f.path, f.content);
          }
        }
        console.log("[handleApplyEdits] Injected missing framework files for hasDayOneFramework");
        // Clear waiting state since we just ensured framework files exist
        setWaitingForScaffold(false);
      }

      setLoadedFileContents(nextContents);

      const fileMapForPreview = new Map(nextContents);
      if (selectedFile) fileMapForPreview.set(selectedFile, editorContent);
      const updatedFiles = Array.from(fileMapForPreview.entries()).map(([name, content]) => ({
        name,
        content,
      }));

      // Safe Edit Loop: validate build before marking done
      setBuilderStatus("building");
      const filesRecord: Record<string, string> = Object.fromEntries(
        updatedFiles.map((f) => [f.name, f.content])
      );
      normalizeFileMapForSandbox(filesRecord);
      const buildResult = await buildSandbox(filesRecord);

      const appliedPaths = result.appliedPaths ?? edits.map((e) => e?.filePath ?? e?.path).filter(Boolean);
      const changedPathsThisApply = [...appliedPaths, ...shellPathsThisRunRef.current];

      if (!buildResult.success) {
        const buildErrorStr = buildResult.error ?? "Build failed";
        // Auto-fix: up to 3 attempts with 30s timeout each — "polishing" hides error during retries
        if (autoFixDepth < 3 && activeProject?.id) {
          setBuilderStatus("polishing");
          // Store error but don't surface to user yet
          setPendingError(friendlyErrorMessage(getBuildErrorSnippet(buildErrorStr, 400)));
          pushBuildEvent("BUILD_RETRY_FIX", { attempt: autoFixDepth + 1 });
          console.log(`[AUTO_FIX] attempt ${autoFixDepth + 1}/3, status: polishing — error: ${buildErrorStr.slice(0, 200)}`);
          const errorSnippet = buildErrorStr.split("\n").filter((l) => l.trim()).slice(0, 5).join("\n");
          const autoFixTimeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 30_000));
          console.log(`[AUTO_FIX] sending ${updatedFiles.length} files with content`);
          const fixPlanPromise = sendInstruction(
            `Fix this esbuild error:\n${errorSnippet}\nOnly modify broken files. Do not touch index.html or src/main.tsx.`,
            activeProject.id,
            updatedFiles,
            projectGraph,
            "fix",
            errorSnippet
          );
          const fixPlan = await Promise.race([fixPlanPromise, autoFixTimeout]);
          if (fixPlan?.edits?.length) {
            return handleApplyEdits(fixPlan.edits, { ...options, isFixAttempt: true, simpleLandingIntent: false, _autoFixDepth: autoFixDepth + 1 });
          }
          if (!fixPlan) console.warn(`[AUTO_FIX] attempt ${autoFixDepth + 1}/3 timed out after 30s`);
        }
        // All 3 auto-fix attempts exhausted — now surface error to user
        console.log("[AUTO_FIX] all attempts exhausted, surfacing error to user");
        allowedTargetPathsRef.current = null;
        allowedPrefixesRef.current = null;
        setBuilderStatus("failed");
        setLastApplySummary(null);
        const errBucket = getBuildErrorBucket(buildErrorStr);
        // Move pendingError to buildValidationError (or compute fresh if no pending)
        setBuildValidationError(pendingError ?? friendlyErrorMessage(getBuildErrorSnippet(buildErrorStr, 400)));
        setPendingError(null);
        setBuildValidationBucket(errBucket);
        const ctx = runContextRef.current;
        if (ctx) {
          setBuildReport({
            runId: ctx.runId,
            status: "failed",
            startedAt: ctx.startedAt,
            finishedAt: Date.now(),
            userPromptSnippet: ctx.userPromptSnippet,
            usedPlan: ctx.usedPlan,
            targetPaths: ctx.targetPaths,
            changedPaths: changedPathsThisApply,
            errorBucket: errBucket,
            lastErrorCode: null,
            landingScopeDroppedCount: landingScopeDroppedCountRef.current,
            landingScopeDroppedPathsSample: [...landingScopeDroppedPathsSampleRef.current],
          });
          pushBuildEvent("BUILD_REPORT_READY");
        }
        shellPathsThisRunRef.current = [];
        if (autoFixDepth >= 3) setFixAttempted(true);
        if (!skipFailToast) {
          toast.error(autoFixDepth >= 3 ? "Build failed after auto-fix." : "Build failed. Click \"Fix errors\" to retry.");
        }
        // CRITICAL FIX: Still trigger preview even on build failure so user sees something
        // The __render.tsx iframe will show the build error in its UI
        console.log("[handleApplyEdits] Build failed but still triggering preview with", updatedFiles.length, "files");
        try {
          await runPreviewWithSession(updatedFiles);
        } catch (previewErr) {
          console.warn("[handleApplyEdits] runPreviewWithSession failed on build error path:", previewErr);
        }
        return { ...result, buildSuccess: false, buildError: buildErrorStr, applyResult: result, updatedFiles };
      }

      allowedTargetPathsRef.current = null;
      allowedPrefixesRef.current = null;
      lastGuardFailureRef.current = {};
      setBuilderStatus("success");
      setBuildValidationError(null);
      setPendingError(null); // Clear any pending error from polishing
      setBuildValidationBucket(null);
      setLastApplySummary(result?.summary ?? "Changes applied");
      // Log success after auto-fix
      if (autoFixDepth > 0) {
        console.log(`[AUTO_FIX] succeeded on attempt ${autoFixDepth}`);
      }

      const ctxSuccess = runContextRef.current;
      if (ctxSuccess) {
        setBuildReport({
          runId: ctxSuccess.runId,
          status: "success",
          startedAt: ctxSuccess.startedAt,
          finishedAt: Date.now(),
          userPromptSnippet: ctxSuccess.userPromptSnippet,
          usedPlan: ctxSuccess.usedPlan,
          targetPaths: ctxSuccess.targetPaths,
          changedPaths: changedPathsThisApply,
          previewReadyAt: Date.now(),
          lastErrorCode: null,
          landingScopeDroppedCount: 0,
          landingScopeDroppedPathsSample: [],
          preflight: preflightMeta,
        });
        pushBuildEvent("BUILD_REPORT_READY");
      }
      shellPathsThisRunRef.current = [];

      if (user && currentProject) {
        await createSnapshot(
          currentProject.id,
          updatedFiles,
          lastPrompt || "After edit",
          "After edit"
        );
        console.log('SNAPSHOT SAVED:', lastPrompt || "After edit");
      }

      try {
        localStorage.setItem(
          `dayone:sandbox:preflight:${sessionId}`,
          JSON.stringify(preflightMeta)
        );
      } catch {
        /* ignore */
      }
      // DEBUG: Log before runPreviewWithSession to trace BUG 2
      console.log("[handleApplyEdits] About to call runPreviewWithSession with", updatedFiles.length, "files:", {
        paths: updatedFiles.slice(0, 15).map((f: any) => f.name),
        hasAppTsx: updatedFiles.some((f: any) => f.name === "src/App.tsx"),
      });
      await runPreviewWithSession(updatedFiles);
      // Mark as booted so boot effect doesn't double-call runPreviewWithSession
      if (activeProject?.id) {
        bootedProjectRef.current = activeProject.id;
      }
      console.log("[handleApplyEdits] runPreviewWithSession COMPLETE");
      setLastPreviewUpdatedAt(Date.now());
      toast.success("Edits applied and build OK");
      incrementBuildCount();

      // Auto-heal: check for runtime errors immediately after preview (unless this is already a fix attempt)
      if (!isFixAttempt) {
        checkForErrorsNow();
      }

      // Post-build: scan for API placeholder strings and notify user
      const placeholdersFound = scanFilesForPlaceholders(updatedFiles);
      if (placeholdersFound.length > 0) {
        const lines = placeholdersFound.map(p => `• **${p.service}** — for ${p.reason}`);
        pushMessage({
          role: "assistant",
          content: `Your app is ready! It uses placeholder API keys for:\n\n${lines.join("\n")}\n\nWhen you're ready to go live, say **"add my API keys"** and I'll help you wire in the real ones.`,
          timestamp: new Date(),
        });
        setPendingKeyPlaceholders(placeholdersFound);
      }

      // Post-build: detect import.meta.env.VITE_ usage and append a helpful chat message
      const envVars = detectEnvVarUsage(updatedFiles);
      if (envVars.length > 0) {
        const serviceNames = [...new Set(envVars.map(envVarToServiceName))];
        const list = serviceNames.length <= 2
          ? serviceNames.join(" and ")
          : serviceNames.slice(0, -1).join(", ") + ", and " + serviceNames[serviceNames.length - 1];
        pushMessage({
          role: "assistant",
          content: `Your app is ready! It uses ${list}. To connect to live data, add your keys in ⚙️ Settings → Environment Variables. Skip this and the app works with sample data.`,
          timestamp: new Date(),
        });
      }

      // Auto-deploy if toggle is ON
      if (autoDeployEnabled && user && currentProject && !isFixAttempt && !isDeploying) {
        toast.info("Auto-deploying...");
        deploy(currentProject.id, updatedFiles, currentProject.name).then(async (deployResult) => {
          if (deployResult.success) {
            toast.success("Auto-deploy complete");
          } else {
            toast.error(`Auto-deploy failed: ${deployResult.error || "Unknown error"}`);
          }
          await addDeployment(
            deployResult.success ? (deployResult.status || "success") : "error",
            deployResult.previewUrl || undefined,
            deployResult.deploymentId,
            undefined,
            deployResult.error,
          );
        });
      }

      const runId = ctxSuccess?.runId;
      const smokeAlreadyRunning = smokeTestStateRef.current?.status === "running";
      const alreadyRanForBuild = runId != null && lastAutoSmokeRunIdRef.current === runId;
      const isUserInitiatedRun = runId != null && runId === lastUserInitiatedRunIdRef.current;
      if (
        debugEnabled &&
        handleRunSmokeTestRef.current &&
        runId &&
        !isFixAttempt &&
        isUserInitiatedRun &&
        !smokeAlreadyRunning &&
        !alreadyRanForBuild
      ) {
        lastAutoSmokeRunIdRef.current = runId;
        handleRunSmokeTestRef.current();
      }
      posthog.capture("build_completed", { project_id: currentProject?.id });
      return { ...result, buildSuccess: true, applyResult: result, updatedFiles };
    } catch (e) {
      allowedTargetPathsRef.current = null;
      allowedPrefixesRef.current = null;
      setBuilderStatus("failed");
      setLastApplySummary(null);
      const msg = e instanceof Error ? e.message : String(e);
      const errBucket = getBuildErrorBucket(msg);
      setBuildValidationError(friendlyErrorMessage(getBuildErrorSnippet(msg, 400)));
      setBuildValidationBucket(errBucket);
      const ctx = runContextRef.current;
      if (ctx) {
        setBuildReport({
          runId: ctx.runId,
          status: "failed",
          startedAt: ctx.startedAt,
          finishedAt: Date.now(),
          userPromptSnippet: ctx.userPromptSnippet,
          usedPlan: ctx.usedPlan,
          targetPaths: ctx.targetPaths,
          changedPaths: [],
          errorBucket: errBucket,
          lastErrorCode: null,
          landingScopeDroppedCount: landingScopeDroppedCountRef.current,
          landingScopeDroppedPathsSample: [...landingScopeDroppedPathsSampleRef.current],
        });
        pushBuildEvent("BUILD_REPORT_READY");
      }
      shellPathsThisRunRef.current = [];
      if (isFixAttempt) setFixAttempted(true);
      throw e;
    }
  };
  handleApplyEditsRef.current = handleApplyEdits;

  // Wire self-healing refs now that handleApplyEdits and sendInstruction are defined
  selfHealApplyRef.current = async (fixes) => {
    const result = await handleApplyEdits(fixes, { isFixAttempt: true, skipFailToast: true });
    return { buildSuccess: result.buildSuccess };
  };
  selfHealFixRef.current = async (errorMessage, errorFile, files) => {
    if (!activeProject?.id) return null;

    // DEFENSIVE: Detect useless error messages that would produce empty prompts
    const isUselessError = !errorMessage ||
      errorMessage.trim() === "{}" ||
      errorMessage.trim() === "[object Object]" ||
      errorMessage.trim().length < 5;

    if (isUselessError) {
      console.error("[selfHealFixRef] Received useless error message:", JSON.stringify(errorMessage));
      logBuildErrorClient({
        projectId: activeProject.id,
        errorClass: "empty_error_message",
        errorMessage: `Empty error reached self-heal: "${errorMessage?.slice(0, 100) || "(null)"}"`,
      });
      // Don't send useless prompt to AI — it will spiral
      return null;
    }

    const errorSnippet = errorMessage.split("\n").filter((l) => l.trim()).slice(0, 10).join("\n");
    const fileContext = errorFile ? `\nThe error is in: ${errorFile}` : "";
    const plan = await sendInstruction(
      `Fix this runtime error. Make the minimum change needed:\n\n${errorSnippet}${fileContext}\n\nDo NOT rewrite files. Only fix the broken line(s).`,
      activeProject.id,
      files,
      projectGraph,
      "fix",
      errorSnippet,
    );
    if (!plan?.edits?.length) return null;
    return {
      edits: plan.edits.map((e: any) => ({
        filePath: e.filePath,
        content: e.content ?? "",
      })),
    };
  };

  const handleRetryWithinTargets = useCallback(() => {
    if (!lastPrompt.trim() || lastFileTargets.length === 0) return;
    const constraint = "\n\nYou must ONLY modify these files: " + lastFileTargets.map((t) => t.path).join(", ");
    handleSendAIMessage({
      prompt: lastPrompt + constraint,
      overrideSimpleLandingIntent: runContextRef.current?.simpleLandingIntent,
    });
    setBuildValidationError(null);
    setBuildValidationBucket(null);
  }, [lastPrompt, lastFileTargets, handleSendAIMessage]);

  const handleRetryLastChange = useCallback(() => {
    if (lastPrompt.trim() && builderStatus !== "generating") {
      handleSendAIMessage({
        prompt: lastPrompt,
        overrideSimpleLandingIntent: runContextRef.current?.simpleLandingIntent,
        fromRetry: true,
      });
    }
  }, [lastPrompt, builderStatus, handleSendAIMessage]);

  const handleCopyBuildReport = useCallback(() => {
    if (!buildReport) return;
    const text = JSON.stringify(buildReport, null, 2);
    navigator.clipboard.writeText(text);
    toast.success("Report copied");
  }, [buildReport]);

  const SMOKE_TEST_PROMPT =
    "Change the Home screen title text to 'Smoke Test ✅' and add a small subtitle 'If you see this, preview works.'";

  type SmokeTestResult = { result: "pass" } | { result: "fail"; failedStep: string; error: string };
  const handleRunSmokeTest = useCallback(async (): Promise<SmokeTestResult> => {
    // Clean state for deterministic smoke test
    previewTelemetryRef.current = null;
    renderAckRef.current = null;
    lastTelemetrySessionIdRef.current = null;
    try {
      localStorage.removeItem(TELEMETRY_KEY_PREFIX + sessionId);
    } catch (_) {}

    if (!activeProject?.id || !handleSendAIMessageRef.current || !iframeRef?.current) {
      const err = "No active project or preview iframe";
      setSmokeTestState({
        status: "fail",
        steps: [{ step: "Started", ts: Date.now(), status: "fail" }, { step: "apply_patch", ts: Date.now(), status: "fail" }],
        failedStep: "apply_patch",
        error: err,
      });
      return { result: "fail", failedStep: "apply_patch", error: err };
    }

    const OVERALL_MS = 45000;
    const APPLY_MS = 20000;
    const BUILD_MS = 20000;
    const HANDSHAKE_MS = 10000;
    const RENDERED_MS = 10000;
    const POLL_MS = 400;
    const HELLO_INTERVAL_MS = 250;
    const isDebug = searchParams.get("debug") === "1";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const previewIframeSrc = `${origin}/__preview?sessionId=${encodeURIComponent(sessionId)}&k=${previewKey}${isDebug ? "&debug=1" : ""}&t=${previewKey}`;
    const renderIframeSrc = `${origin}/__render?sessionId=${encodeURIComponent(sessionId)}&t=${previewKey}${isDebug ? "&debug=1" : ""}`;

    setSmokeTestState({ status: "running", steps: [{ step: "Started", ts: Date.now() }] });

    const addStep = (step: SmokeTestStep | string, status?: "ok" | "fail") => {
      setSmokeTestState((prev) => ({
        ...prev,
        steps: [...prev.steps, { step, ts: Date.now(), status }],
      }));
    };

    const fail = (failedStep: SmokeTestStep | string, error: string, runId?: string) => {
      setSmokeTestState((prev) => ({
        ...prev,
        status: "fail",
        failedStep,
        error,
        runId,
        lastTelemetrySessionId: lastTelemetrySessionIdRef.current ?? undefined,
        previewIframeSrc,
        renderIframeSrc,
        expectedSessionId: sessionId,
      }));
    };

    const pass = () => {
      setSmokeTestState((prev) => ({
        ...prev,
        status: "pass",
        steps: [...prev.steps, { step: "rendered", ts: Date.now(), status: "ok" }],
      }));
    };

    try {
      handleSendAIMessageRef.current(SMOKE_TEST_PROMPT);
      const start = Date.now();
      const applyDeadline = start + APPLY_MS;

      const poll = (): Promise<SmokeTestResult> =>
        new Promise((resolve) => {
          const tick = () => {
            const status = builderStatusRef.current;
            const report = buildReportRef.current;

            if (Date.now() - start > OVERALL_MS) {
              const err = "Smoke test timeout (45s overall)";
              addStep("apply_patch", "fail");
              addStep("build", "fail");
              fail("apply_patch", err, report?.runId);
              resolve({ result: "fail", failedStep: "apply_patch", error: err });
              return;
            }

            if (status === "failed") {
              const bucket = report?.errorBucket ?? buildValidationBucketRef.current ?? "unknown";
              const err = buildValidationErrorRef.current ?? report?.errorBucket ?? "Build failed";
              const step: SmokeTestStep =
                bucket?.startsWith("PATCH_GUARD") || bucket?.includes("apply") ? "apply_patch" : "build";
              addStep(step, "fail");
              fail(step, err, report?.runId);
              resolve({ result: "fail", failedStep: String(step), error: err });
              return;
            }

            if (Date.now() > applyDeadline && status !== "success") {
              const err = `Apply/build timeout (${APPLY_MS / 1000}s). Status: ${status}`;
              addStep("apply_patch", "fail");
              addStep("build", "fail");
              fail("apply_patch", err, report?.runId);
              resolve({ result: "fail", failedStep: "apply_patch", error: err });
              return;
            }

            if (status === "success" && report?.status === "success") {
              addStep("apply_patch", "ok");
              addStep("build", "ok");
              previewTelemetryRef.current = null;
              renderAckRef.current = null;
              setPreviewKey((k) => k + 1);

              const handshakeStart = Date.now();
              const nonce = crypto.randomUUID();
              const previewWin = iframeRef.current?.contentWindow;

              const sendHello = () => {
                if (previewWin) {
                  previewWin.postMessage({ type: "DAYONE_HOST_HELLO", sessionId, nonce, ts: Date.now() }, "*");
                }
              };

              const checkHandshake = () => {
                try {
                  const raw = localStorage.getItem(TELEMETRY_KEY_PREFIX + sessionId);
                  if (raw) {
                    const parsed = JSON.parse(raw) as { sessionId?: string; ts?: number; rendered?: boolean; bootStep?: string; lastError?: string; error?: string };
                    if (parsed && (parsed.sessionId != null || parsed.ts != null)) {
                      lastTelemetrySessionIdRef.current = parsed.sessionId ?? null;
                      previewTelemetryRef.current = {
                        sessionId: parsed.sessionId ?? "",
                        ts: parsed.ts ?? Date.now(),
                        rendered: Boolean(parsed.rendered),
                        bootStep: parsed.bootStep,
                        lastError: parsed.lastError,
                        error: parsed.error,
                      };
                    }
                  }
                } catch (_) {}
                const elapsed = Date.now() - handshakeStart;
                if (elapsed > HANDSHAKE_MS) {
                  const ack = renderAckRef.current;
                  const t = previewTelemetryRef.current;
                  if (ack && ack.nonce === nonce) {
                    addStep("preview_handshake", "ok");
                    if (ack.rendered) {
                      pass();
                      resolve({ result: "pass" });
                      return;
                    }
                    const detail = [ack.bootStep ? `bootStep: ${ack.bootStep}` : "", ack.lastError].filter(Boolean).join(" | ");
                    fail("rendered", detail || "ACK received but rendered=false", report?.runId);
                    resolve({ result: "fail", failedStep: "rendered", error: detail || "ACK received but rendered=false" });
                    return;
                  } else if (t) {
                    addStep("preview_handshake", "ok");
                    if (t.rendered) {
                      pass();
                      resolve({ result: "pass" });
                      return;
                    }
                    const detail = [t.bootStep ? `bootStep: ${t.bootStep}` : "", t.error || t.lastError].filter(Boolean).join(" | ");
                    fail("rendered", detail || "No rendered", report?.runId);
                    resolve({ result: "fail", failedStep: "rendered", error: detail || "No rendered" });
                    return;
                  } else {
                    const parts = [
                      "No ACK within 10s",
                      `expectedSid=${sessionId}`,
                      lastTelemetrySessionIdRef.current ? `lastSid=${lastTelemetrySessionIdRef.current}` : "no telemetry",
                    ];
                    const err = parts.join("; ");
                    fail("preview_handshake", err, report?.runId);
                    resolve({ result: "fail", failedStep: "preview_handshake", error: err });
                  }
                  return;
                }
                const ack = renderAckRef.current;
                if (ack && ack.nonce === nonce) {
                  addStep("preview_handshake", "ok");
                  if (ack.rendered) {
                    pass();
                    resolve({ result: "pass" });
                    return;
                  }
                  const renderedStart = Date.now();
                  const waitRendered = () => {
                    if (Date.now() - renderedStart > RENDERED_MS) {
                      const detail = [ack.bootStep ? `bootStep: ${ack.bootStep}` : "", ack.lastError].filter(Boolean).join(" | ");
                      fail("rendered", detail || "ACK received but rendered=false", report?.runId);
                      resolve({ result: "fail", failedStep: "rendered", error: detail || "ACK received but rendered=false" });
                      return;
                    }
                    const t2 = previewTelemetryRef.current;
                    if (t2?.rendered) {
                      pass();
                      resolve({ result: "pass" });
                      return;
                    }
                    const a2 = renderAckRef.current;
                    if (a2?.rendered) {
                      pass();
                      resolve({ result: "pass" });
                      return;
                    }
                    setTimeout(waitRendered, 150);
                  };
                  setTimeout(waitRendered, 150);
                  return;
                }
                const t = previewTelemetryRef.current;
                if (t) {
                  addStep("preview_handshake", "ok");
                  if (t.rendered) {
                    pass();
                    resolve({ result: "pass" });
                    return;
                  }
                  const detail = [t.bootStep ? `bootStep: ${t.bootStep}` : "", t.error || t.lastError].filter(Boolean).join(" | ");
                  if (detail) {
                    fail("rendered", detail, report?.runId);
                    resolve({ result: "fail", failedStep: "rendered", error: detail });
                    return;
                  }
                }
                sendHello();
                setTimeout(checkHandshake, HELLO_INTERVAL_MS);
              };
              sendHello();
              setTimeout(checkHandshake, HELLO_INTERVAL_MS);
              return;
            }

            setTimeout(tick, POLL_MS);
          };
          tick();
        });

      return await poll();
    } catch (e) {
      const err = e instanceof Error ? e.message : String(e);
      addStep("apply_patch", "fail");
      fail("apply_patch", err);
      return { result: "fail", failedStep: "apply_patch", error: err };
    }
  }, [activeProject?.id, iframeRef, sessionId, previewKey, searchParams]);
  handleRunSmokeTestRef.current = handleRunSmokeTest;

  const [run5xState, setRun5xState] = useState<{
    running: boolean;
    passed: number;
    total: number;
    failures?: Array<{ runIndex: number; failedStep: string; error: string }>;
  } | null>(null);
  const handleRunSmokeTest5x = useCallback(async () => {
    if (!handleRunSmokeTestRef.current) return;
    run5xCancelledRef.current = false;
    setRun5xState({ running: true, passed: 0, total: 5, failures: [] });
    const failures: Array<{ runIndex: number; failedStep: string; error: string }> = [];
    let passed = 0;
    for (let i = 0; i < 5; i++) {
      if (run5xCancelledRef.current) break;
      const result = await handleRunSmokeTestRef.current();
      if (result.result === "pass") passed++;
      else failures.push({ runIndex: i + 1, failedStep: result.failedStep, error: result.error });
      setRun5xState({ running: i < 4 && !run5xCancelledRef.current, passed, total: i + 1, failures: [...failures] });
    }
    setRun5xState({ running: false, passed, total: 5, failures: failures.length > 0 ? failures : undefined });
  }, []);

  const handleDismissOnboarding = useCallback(() => {
    const key = user?.id ? `dayone.onboarding.workspace.dismissed.${user.id}` : "dayone.onboarding.workspace.dismissed";
    localStorage.setItem(key, "true");
    setOnboardingDismissed(true);
  }, [user?.id]);

  const projectFileContents = useMemo(() => {
    const list = getProjectFiles();
    return Object.fromEntries(list.map((f) => [f.name, f.content]));
  }, [getProjectFiles]);

  const triggerFixBuild = useCallback(async () => {
    if (!activeProject?.id) {
      toast.error("No active project selected");
      return;
    }
    if (builderStatus === "generating" || builderStatus === "applying" || builderStatus === "building" || builderStatus === "fixing" || builderStatus === "polishing" || fixAttempted) return;

    setFixAttempted(true);
    setBuilderStatus("fixing");
    const maxAttempts = 3;
    let lastBuildSuccess = false;
    let projectFiles = getProjectFiles();

    try {
      const last = lastGuardFailureRef.current;
      let guardInstruction = "";
      if (last?.errorCode === "PATCH_GUARD_LIMIT" && last?.maxPatchItems != null)
        guardInstruction += `\n\nRegenerate edits with <= ${last.maxPatchItems} items, no duplicates, only repo-relative paths.`;
      if (last?.errorCode === "PATCH_GUARD_INVALID_PATH")
        guardInstruction += "\n\nOnly use repo-relative paths like src/..., no leading '/', no './', no backslashes, no '..'.";
      if (last?.errorCode === "PATCH_GUARD_OUT_OF_SCOPE")
        guardInstruction += "\n\nYou attempted edits outside allowed scope. ONLY modify the allowed target files/prefixes listed. Do not create new files. If more files are needed, request expanding file targets." +
          "\n\nYour last attempt tried to edit out-of-scope files. Only edit src/App.tsx, src/pages/LandingPage.tsx, and files under src/components/ui/. Do not touch any other paths.";
      if (last?.errorCode === "PATCH_GUARD_INVALID_EXTENSION")
        guardInstruction += "\n\nDo not create .js/.jsx files in src/. Use .tsx for React components and .ts for utilities.";
      if (last?.errorCode === "PATCH_GUARD_FORBIDDEN_IMPORT")
        guardInstruction += "\n\nDo NOT use esm.sh or any CDN URL imports. Use local 'react' and 'react-dom/client' imports only and relative .tsx imports.";
      if (last?.errorCode === "PATCH_GUARD_PROTECTED_FILE")
        guardInstruction += "\n\nDo NOT edit index.html or src/main.tsx. They are protected and cannot be modified by the builder.";
      if (landingFilterDroppedRef.current)
        guardInstruction += "\n\nSome out-of-scope edits were automatically discarded. Only edit src/App.tsx, src/pages/LandingPage.tsx, and files under src/components/ui/.";
      const allowed = allowedTargetPathsRef.current;
      const prefixes = allowedPrefixesRef.current;
      let scopeInstruction = "";
      if (allowed?.size || (prefixes?.length ?? 0)) {
        const fromAllowed = (allowed && allowed.size <= 30) ? [...allowed].slice(0, 30) : [];
        const fromPrefixes = prefixes?.length ? prefixes : [];
        const list = [...fromAllowed, ...fromPrefixes].join(", ");
        if (list) scopeInstruction = `\n\nYou may ONLY modify files within these allowed paths/prefixes:\n${list}\n\nDo not create or reference files outside allowed scope. No hallucinated paths.`;
      }
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        console.log("[AUTO_FIX_ATTEMPT]", attempt);

        const errorDetail = buildValidationError || runtimeErrorSnippet || "Build failed";
        const fixMessage = `RUNTIME ERROR: ${errorDetail}\n\nFix only the broken component. Do not rewrite other files. Do not restructure routing. Make the minimum change needed to resolve this error.` + guardInstruction + scopeInstruction;
        const plan = await sendInstruction(
          fixMessage,
          activeProject.id,
          projectFiles,
          projectGraph,
          "fix",
          errorDetail,
          {
            filterForLanding: runContextRef.current?.simpleLandingIntent ?? false,
            onLandingFilterDropped: (n) => {
              landingFilterDroppedRef.current = true;
              landingFilterDroppedCountRef.current = n;
            },
          }
        );

        if (!plan?.edits?.length) {
          toast.error("No fix suggested. Try editing the files manually.");
          setBuilderStatus("failed");
          return;
        }

        const outcome = await handleApplyEdits(plan.edits, {
          isFixAttempt: true,
          skipFailToast: true,
          simpleLandingIntent: runContextRef.current?.simpleLandingIntent ?? false,
        });
        if (outcome.buildSuccess) {
          lastBuildSuccess = true;
          break;
        }

        if (outcome.errorCode === "PATCH_GUARD_LIMIT" && outcome.maxPatchItems != null) {
          lastGuardFailureRef.current = { errorCode: outcome.errorCode, maxPatchItems: outcome.maxPatchItems };
          guardInstruction = `\n\nRegenerate edits with <= ${outcome.maxPatchItems} items, no duplicates, only repo-relative paths.`;
        } else if (outcome.errorCode === "PATCH_GUARD_INVALID_PATH") {
          lastGuardFailureRef.current = { errorCode: outcome.errorCode, maxPatchItems: outcome.maxPatchItems };
          guardInstruction = "\n\nOnly use repo-relative paths like src/..., no leading '/', no './', no backslashes, no '..'.";
        } else if (outcome.errorCode === "PATCH_GUARD_OUT_OF_SCOPE") {
          lastGuardFailureRef.current = { errorCode: outcome.errorCode, maxPatchItems: outcome.maxPatchItems };
          guardInstruction = "\n\nYou attempted edits outside allowed scope. ONLY modify the allowed target files/prefixes listed. Do not create new files. If more files are needed, request expanding file targets." +
            "\n\nYour last attempt tried to edit out-of-scope files. Only edit src/App.tsx, src/pages/LandingPage.tsx, and files under src/components/ui/. Do not touch any other paths.";
        } else if (outcome.errorCode === "PATCH_GUARD_INVALID_EXTENSION") {
          lastGuardFailureRef.current = { errorCode: outcome.errorCode, maxPatchItems: outcome.maxPatchItems };
          guardInstruction = "\n\nDo not create .js/.jsx files in src/. Use .tsx for React components and .ts for utilities.";
        } else if (outcome.errorCode === "PATCH_GUARD_FORBIDDEN_IMPORT") {
          lastGuardFailureRef.current = { errorCode: outcome.errorCode, maxPatchItems: outcome.maxPatchItems };
          guardInstruction = "\n\nDo NOT use esm.sh or any CDN URL imports. Use local 'react' and 'react-dom/client' imports only and relative .tsx imports.";
        } else if (outcome.errorCode === "PATCH_GUARD_PROTECTED_FILE") {
          lastGuardFailureRef.current = { errorCode: outcome.errorCode, maxPatchItems: outcome.maxPatchItems };
          guardInstruction = "\n\nDo NOT edit index.html or src/main.tsx. They are protected and cannot be modified by the builder.";
        }
        if (landingFilterDroppedRef.current)
          guardInstruction += "\n\nSome out-of-scope edits were automatically discarded. Only edit src/App.tsx, src/pages/LandingPage.tsx, and files under src/components/ui/.";
        setBuildValidationError(friendlyErrorMessage(getBuildErrorSnippet(outcome.buildError ?? "", 400)));
        setBuildValidationBucket(getBuildErrorBucket(outcome.buildError ?? ""));
        if (outcome.updatedFiles?.length) {
          projectFiles = outcome.updatedFiles.map((f) => ({ name: f.name, content: f.content }));
        }
      }

      if (!lastBuildSuccess) {
        toast.error("Auto-fix failed after 3 attempts");
        setBuilderStatus("failed");
      }
    } catch (e) {
      toast.error(friendlyErrorMessage(e instanceof Error ? e.message : "Fix request failed"));
      setBuilderStatus("failed");
    }
  }, [
    activeProject,
    builderStatus,
    buildValidationError,
    buildValidationBucket,
    getProjectFiles,
    sendInstruction,
    projectGraph,
    fixAttempted,
    handleApplyEdits,
  ]);

  const handleRestoreSnapshot = async (snapshot: ProjectSnapshot) => {
    if (!user || !currentProject) {
      toast.error("Cannot restore: no user or project");
      return;
    }

    try {
      console.log(`[handleRestoreSnapshot] Starting restore of snapshot ${snapshot.id} with ${snapshot.files_snapshot?.length ?? 0} files`);

      // Validate snapshot data
      if (!Array.isArray(snapshot.files_snapshot)) {
        const errMsg = `Snapshot files_snapshot is not an array: ${typeof snapshot.files_snapshot}`;
        console.error(`[handleRestoreSnapshot] ${errMsg}`);
        toast.error(`Restore failed: ${errMsg}`);
        return;
      }

      if (snapshot.files_snapshot.length === 0) {
        toast.error("Snapshot has no files to restore");
        return;
      }

      // Build set of paths in the snapshot
      const snapshotPaths = new Set(snapshot.files_snapshot.map(f => f.name));

      // Delete files that exist now but aren't in the snapshot (removes corrupted files)
      // Refresh file list first to get current state
      await listFiles();
      const currentFiles = files || [];
      for (const file of currentFiles) {
        const path = file.path ?? file.name;
        if (path && !snapshotPaths.has(path)) {
          console.log(`[handleRestoreSnapshot] Deleting extra file not in snapshot: ${path}`);
          await deleteFile(path);
        }
      }

      // Restore files from snapshot
      for (const file of snapshot.files_snapshot) {
        await saveFile(file.name, file.content);
      }
      const newContents = new Map<string, string>();
      for (const file of snapshot.files_snapshot) {
        newContents.set(file.name, file.content);
      }
      setLoadedFileContents(newContents);

      await runPreviewWithSession(snapshot.files_snapshot);
      toast.success(`Restored to "${snapshot.label || 'previous version'}" (${snapshot.files_snapshot.length} files)`);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      console.error(`[handleRestoreSnapshot] Error:`, err);
      toast.error(`Restore failed: ${errMsg}`);
    }
  };

  const handleRevertLastChange = useCallback(async () => {
    run5xCancelledRef.current = true;
    if (snapshots.length < 2) return;
    pushBuildEvent("REVERT_LAST");
    await handleRestoreSnapshot(snapshots[1]);
  }, [snapshots, pushBuildEvent, handleRestoreSnapshot]);

  const handleExportZip = useCallback(async () => {
    const files = getProjectFiles();
    if (files.length === 0) {
      toast.error("No files to export");
      return;
    }
    pushBuildEvent("EXPORT_ZIP");
    try {
      await downloadProjectAsZip(files, activeProject?.name || activeProject?.id || "project");
      toast.success("Exported as ZIP");
    } catch (err) {
      toast.error(friendlyErrorMessage(err instanceof Error ? err.message : "Export failed"));
    }
  }, [getProjectFiles, activeProject?.name, activeProject?.id, pushBuildEvent]);

  // Pre-deploy placeholder key modal state
  const [preDeployKeys, setPreDeployKeys] = useState<DetectedPlaceholder[]>([]);
  const [preDeployKeyValues, setPreDeployKeyValues] = useState<Record<string, string>>({});
  const [preDeployModalOpen, setPreDeployModalOpen] = useState(false);
  const preDeployResolveRef = useRef<(() => void) | null>(null);

  const handleDeploy = async () => {
    if (!user || !currentProject) {
      toast.error("Deploy requires sign-in (not available in dev mode).");
      return;
    }

    // Check for placeholder keys before deploying
    const files = getProjectFiles();
    const placeholders = scanFilesForPlaceholders(files);
    if (placeholders.length > 0) {
      const missing = placeholders.filter(p => {
        const provided = collectedApiKeysRef.current[p.keyName];
        return !provided || provided.startsWith("YOUR_");
      });
      if (missing.length > 0) {
        setPreDeployKeys(missing);
        setPreDeployKeyValues({});
        setPreDeployModalOpen(true);
        // Wait for user to dismiss or submit keys
        await new Promise<void>(resolve => { preDeployResolveRef.current = resolve; });
      }
    }

    setDeployDialogOpen(true);
    const result = await deploy(currentProject.id, files, currentProject.name);
    if (result.success) {
      posthog.capture("app_published", { project_id: currentProject.id, preview_url: result.previewUrl });
      // Save a deploy snapshot with the deploy URL
      const deployLabel = `Deploy ${new Date().toLocaleDateString()}`;
      await createSnapshot(
        currentProject.id,
        files,
        "Published to Vercel",
        deployLabel,
        result.previewUrl || result.url
      );
    }
    // Record in deployment history
    await addDeployment(
      result.success ? (result.status || "success") : "error",
      result.previewUrl || undefined,
      result.deploymentId,
      undefined,
      result.error,
    );
  };

  const handlePreDeployKeysSubmit = () => {
    // Store any provided keys
    for (const [k, v] of Object.entries(preDeployKeyValues)) {
      if (v.trim()) collectedApiKeysRef.current[k] = v.trim();
    }

    // If any real keys were provided, queue an edit to replace placeholders
    const realKeys = Object.entries(preDeployKeyValues).filter(([, v]) => v.trim());
    if (realKeys.length > 0) {
      const keySection = realKeys.map(([k, v]) => `${k}: ${v.trim()}`).join("\n");
      const editPrompt = `Replace all API key placeholder values with the real keys provided below. Only change the placeholder strings, nothing else.\n\n[API KEYS]\n${keySection}`;
      setTimeout(() => handleSendAIMessageRef.current?.(editPrompt), 100);
    }

    setPreDeployModalOpen(false);
    preDeployResolveRef.current?.();
    preDeployResolveRef.current = null;
  };

  const handlePreDeployKeysSkip = () => {
    setPreDeployModalOpen(false);
    preDeployResolveRef.current?.();
    preDeployResolveRef.current = null;
  };

  const handleAddCustomDomain = async () => {
    if (!currentProject?.id || !customDomainInput.trim()) return;
    if (!canUseCustomDomain) {
      toast.error("Custom domains require a Pro plan. Upgrade to use custom domains.");
      return;
    }
    const result = await addCustomDomain(currentProject.id, customDomainInput.trim(), currentProject.name);
    if (result.domain) {
      setSavedCustomDomain(result.domain);
      try { localStorage.setItem(CUSTOM_DOMAIN_KEY(currentProject.id), result.domain); }
      catch { /* ignore */ }
      setCustomDomainInput("");
      toast.success(`Domain ${result.domain} added`);
    } else if (result.error) {
      toast.error(result.error);
    }
  };

  const handleCheckDomain = async () => {
    if (!currentProject?.id || !savedCustomDomain) return;
    await checkDomainStatus(currentProject.id, savedCustomDomain, currentProject.name);
  };

  const handleNewProject = () => {
    if (!user && isLocalhost) setDevProject(null);
    navigate("/code-workspace");
  };

  const handleSelectProject = async (project: any) => {
    navigate(`/code-workspace?projectId=${project.id}`);
    await selectProject(project);
  };

  const handleEditorMount = (editor: any) => {
    editorRef.current = editor;
  };

  // Auto-trigger deploy when arriving via ?action=deploy (from My Apps)
  useEffect(() => {
    if (actionFromUrl === "deploy" && activeProject && !loading && !actionDeployFiredRef.current) {
      actionDeployFiredRef.current = true;
      handleDeploy();
    }
  }, [actionFromUrl, activeProject?.id, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleSave]);

  if (!user && !isLocalhost) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Sign in Required</h2>
          <p className="text-muted-foreground">Please sign in to access the Builder</p>
          <div className="mt-4">
            <Button onClick={() => navigate("/signin")}>Go to Sign in</Button>
          </div>
        </div>
      </div>
    );
  }

  if (user && loading && projects.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isGeneratingProject) {
    return (
      <>
        <SEOHead title="Building Your App | DayOne" description="AI is generating your application" />
        <BuilderBuildingState progress={generationProgress} logs={generationLogs} />
      </>
    );
  }

  if (!projectIdFromUrl && !activeProject) {
    return (
      <>
        <SEOHead title="Builder | DayOne" description="Build your app with AI" />
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-hero">
          <div className="text-center max-w-sm px-4">
            <p className="text-muted-foreground mb-4">Start by creating an app from a prompt.</p>
            <Button onClick={() => navigate("/create")}>Go to Create</Button>
          </div>
        </div>
      </>
    );
  }

  if (projectIdFromUrl && !activeProject) {
    if (loading || !projectLoadAttempted || (!user && isLocalhost)) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-hero gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground text-sm">Loading project...</p>
        </div>
      );
    }

    if (projectLoadError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-hero gap-4">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold mb-2">Project Not Found</h2>
            <p className="text-muted-foreground mb-2">{projectLoadError}</p>
            <div className="flex gap-3 justify-center mt-4">
              <Button variant="outline" onClick={() => navigate("/my-apps")}>
                View My Apps
              </Button>
              <Button onClick={() => navigate("/code-workspace")}>Create New Project</Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!activeProject) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const buildStartedThisSession = buildStartedThisSessionRef.current;
  const hasFramework = hasDayOneFramework(loadedFileContents);
  if (waitingForScaffold && buildStartedThisSession) {
    const debugHudProps = {
      builderStatus,
      waitingForScaffold,
      loadedFileContentsSize: loadedFileContents.size,
      hasFramework,
      isPreviewAvailable: hasFramework || Boolean(previewUrl || sandboxRunning),
      previewUrl: previewUrl ?? undefined,
      sandboxRunning,
      activeProjectId: activeProject?.id,
      projectIdFromUrl,
      lastBuildSendTimestamp: buildLog[0]?.ts,
      lastPreviewBootedAt,
      recentBuilderEvents: buildLog,
      builderEvents,
      lastFileTargets,
      landingFilterDroppedCount: landingFilterDroppedCountRef.current,
      patchGuardErrorCode: buildReport?.lastErrorCode ?? null,
      landingScopeDroppedCount: buildReport?.landingScopeDroppedCount ?? landingScopeDroppedCountRef.current,
      landingScopeDroppedPathsSample: buildReport?.landingScopeDroppedPathsSample ?? [...landingScopeDroppedPathsSampleRef.current],
    };
    if (scaffoldWaitTimedOut) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-hero gap-4">
          <div className="text-center max-w-sm">
            <h2 className="text-xl font-semibold mb-2">Preview waiting for files</h2>
            <p className="text-muted-foreground text-sm mb-4">
              Scaffold files are taking longer than expected. You can reload the preview or open the workspace and send a prompt to generate files.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Button variant="outline" size="sm" onClick={() => { setScaffoldWaitTimedOut(false); handleRefreshPreview(); }}>
                Reload preview
              </Button>
              <Button variant="secondary" size="sm" onClick={() => { setWaitingForScaffold(false); setScaffoldWaitTimedOut(false); }}>
                Open workspace
              </Button>
            </div>
          </div>
          {searchParams.get("debug") === "1" && <BuilderDebugHUD {...debugHudProps} />}
        </div>
      );
    }
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-hero gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Generating your app...</h2>
          <p className="text-muted-foreground text-sm">
            Waiting for framework files ({loadedFileContents.size} files)
          </p>
        </div>
        {searchParams.get("debug") === "1" && <BuilderDebugHUD {...debugHudProps} />}
      </div>
    );
  }

  const isPreviewAvailable =
    bootedProjectRef.current === activeProject?.id ||
    hasFramework ||
    Boolean(previewUrl || sandboxRunning);


  return (
    <>
      <SEOHead
        title={`${activeProject.name || "Project"} | DayOne Builder`}
        description="Build and edit your app with AI"
      />

      <BuilderErrorBoundary projectId={activeProject?.id} projectName={activeProject?.name}>
        <BuilderWorkspaceState
          project={activeProject}
          projects={projects}
          onSelectProject={handleSelectProject}
          onNewProject={handleNewProject}
          onRenameProject={renameProject}
          onDeleteProject={deleteProject}
          onDuplicateProject={duplicateProject}
          files={files}
          selectedFile={selectedFile}
          onSelectFile={handleFileSelect}
          previewUrl={previewUrl}
          iframeRef={iframeRef}
          previewKey={previewKey}
          previewSessionId={sessionId}
          isPreviewAvailable={isPreviewAvailable}
          messages={aiMessages}
          builderStatus={builderStatus}
          onSendMessage={handleSendAIMessage}
          onApplyEdits={handleApplyEdits}
          lastPreviewUpdatedAt={lastPreviewUpdatedAt}
          editorContent={editorContent}
          onEditorChange={setEditorContent}
          onEditorMount={handleEditorMount}
          onDeploy={handleDeploy}
          isDeploying={isDeploying}
          previewReady={sandboxRunning}
          onRefreshPreview={handleRefreshPreview}
          onPreviewReady={() => {
            setLastPreviewReadyAt(Date.now());
            pushBuildEvent("PREVIEW_READY");
          }}
          memory={projectMemory}
          isMemoryLoading={isMemoryLoading}
          snapshots={snapshots}
          snapshotsLoading={snapshotsLoading}
          onRestoreSnapshot={handleRestoreSnapshot}
          onDeleteSnapshot={deleteSnapshot}
          buildValidationError={buildValidationError}
          buildValidationBucket={buildValidationBucket}
          onFixBuildClick={triggerFixBuild}
          fixAttempted={fixAttempted}
          lastApplySummary={lastApplySummary}
          lastPrompt={lastPrompt}
          onRetryLastChange={handleRetryLastChange}
          onRetryWithinTargets={handleRetryWithinTargets}
          onRevertLastChange={handleRevertLastChange}
          onExportZip={handleExportZip}
          githubConnected={githubConnection.connected}
          githubHasPat={githubConnection.connected}
          onOpenGitHubConnect={() => setGitHubConnectModalOpen(true)}
          onOpenGitHubPush={() => setGitHubPushModalOpen(true)}
          githubPushRunning={githubPushRunning}
          projectFileContents={projectFileContents}
          onDismissPlan={dismissLastPlan}
          buildLog={buildLog}
          promptPreFill={promptPreFill}
          waitingForScaffold={waitingForScaffold}
          buildStartedThisSession={buildStartedThisSession}
          runtimeErrorSnippet={runtimeErrorSnippet}
          lastPreviewReadyAt={lastPreviewReadyAt}
          onPlanGenerated={() => pushBuildEvent("PLAN_GENERATED")}
          entrySource={navState?.source}
          onPlanAutoGenerated={(source) => pushBuildEvent(source === "create" ? "PLAN_AUTO_GENERATED_CREATE" : "PLAN_AUTO_GENERATED")}
          buildReport={buildReport}
          onCopyBuildReport={handleCopyBuildReport}
          shouldShowOnboarding={!onboardingDismissed && (navState?.source === "create" || navState?.source === "idea")}
          onDismissOnboarding={handleDismissOnboarding}
          lastRepo={lastRepo}
          createdScreenStubs={createdScreenStubs}
          streamingProgress={streamingProgress}
          planPreview={planPreview}
          lastDeployUrl={lastDeployUrl}
          selfHealState={selfHealState}
          onSelfHealFix={triggerManualFix}
          onStartFresh={() => {
            resetSelfHeal();
            setBuilderStatus("idle");
            setBuildValidationError(null);
            setBuildValidationBucket(null);
            setFixAttempted(false);
          }}
          securityAlerts={securityAlerts}
          onDismissSecurityAlerts={clearSecurityAlerts}
          narrativeMessages={narrativeMessages}
          chatMode={chatMode}
          onChatModeChange={setChatMode}
          chatLoading={chatLoading}
        />
      </BuilderErrorBoundary>


      <Dialog open={githubConnectModalOpen} onOpenChange={setGitHubConnectModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Connect GitHub</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {githubConnection.connected ? (
              <>
                <p className="text-sm text-green-700 dark:text-green-400">
                  Connected as <span className="font-mono font-medium">{githubConnection.username}</span>
                </p>
                <div className="flex gap-2 justify-between">
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={handleGitHubDisconnect}>
                    Disconnect GitHub
                  </Button>
                  <Button variant="outline" onClick={() => setGitHubConnectModalOpen(false)}>
                    Close
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Link your GitHub account to push projects to a repository.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="github-pat">Personal Access Token</Label>
                    <a
                      href="https://github.com/settings/tokens?type=beta"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                    >
                      Create token <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  <Input
                    id="github-pat"
                    type="password"
                    placeholder="github_pat_…"
                    value={githubConnectPatInput}
                    onChange={(e) => setGitHubConnectPatInput(e.target.value)}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Fine-grained token with <span className="font-mono">Contents</span> (read & write) permission. Encrypted and stored securely.
                  </p>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setGitHubConnectModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleGitHubConnectContinue} disabled={!githubConnectPatInput.trim()}>
                    Save token
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={githubPushModalOpen}
        onOpenChange={(open) => {
          setGitHubPushModalOpen(open);
          if (!open) githubResetPushResult();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Push to GitHub</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {githubPushResult?.repoUrl && (
              <div className="space-y-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <p className="text-sm font-medium text-green-700 dark:text-green-400">Pushed successfully</p>
                <p className="text-xs text-muted-foreground">
                  Pushed {githubPushResult.filesCount ?? 0} files
                  {(githubPushResult.skippedCount ?? 0) > 0 && `, skipped ${githubPushResult.skippedCount}`}.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" asChild>
                    <a href={githubPushResult.repoUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      Open on GitHub
                    </a>
                  </Button>
                  {(() => {
                    const m = githubPushResult.repoUrl?.match(/github\.com\/([^/]+)\/([^/]+)/);
                    const vercelUrl = m ? `https://vercel.com/new/import?s=https://github.com/${m[1]}/${m[2]}` : null;
                    return vercelUrl ? (
                      <Button size="sm" variant="secondary" asChild>
                        <a href={vercelUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3.5 w-3.5 mr-1" />
                          Deploy on Vercel
                        </a>
                      </Button>
                    ) : null;
                  })()}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(githubPushResult.repoUrl!);
                      toast.success("Repo URL copied");
                    }}
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" />
                    Copy repo URL
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setGitHubPushModalOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>
            )}
            {githubPushResult?.error && (
              <p className="text-sm text-destructive">{githubPushResult.error}</p>
            )}
            {!githubPushResult && (
              <>
                {githubPushRunning ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {githubPushProgress || "Pushing…"}
                  </div>
                ) : (
                  <>
                    {(() => {
                      const files = getProjectFiles();
                      const dryRun = filterFilesForPush(files);
                      return (
                        <div className="space-y-2 p-2 rounded-lg bg-muted/30 border border-border text-xs">
                          <p className="font-medium">Dry run</p>
                          <p className="text-muted-foreground">Will push: {dryRun.toPush.length} files</p>
                          {dryRun.skipped.length > 0 && (
                            <>
                              <p className="text-muted-foreground">Skipped: {dryRun.skipped.length} files</p>
                              <details className="mt-1">
                                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Show skipped</summary>
                                <ul className="mt-1 list-disc list-inside text-muted-foreground max-h-24 overflow-y-auto">
                                  {dryRun.skipped.map((s, i) => (
                                    <li key={i}>{s.path} — {s.reason}</li>
                                  ))}
                                </ul>
                              </details>
                            </>
                          )}
                        </div>
                      );
                    })()}
                    {githubLinkedRepo && (
                      <div className="space-y-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                        <p className="text-xs font-medium text-blue-700 dark:text-blue-400">
                          Linked repo: <span className="font-mono">{githubLinkedRepo.repoFullName}</span>
                        </p>
                        {githubLinkedRepo.lastPushedAt && (
                          <p className="text-xs text-muted-foreground">
                            Last pushed: {new Date(githubLinkedRepo.lastPushedAt).toLocaleDateString()}
                          </p>
                        )}
                        <Button
                          className="w-full"
                          onClick={async () => {
                            pushBuildEvent("GITHUB_PUSH_STARTED", { repo: githubLinkedRepo.repoFullName });
                            const files = getProjectFiles();
                            const result = await pushToGitHub(files, activeProject?.name || "project", {
                              repoFullName: githubLinkedRepo.repoFullName,
                            });
                            if (result?.success) {
                              pushBuildEvent("GITHUB_PUSH_DONE", { url: result.repoUrl });
                              toast.success("Pushed to GitHub");
                            } else if (result?.error) {
                              pushBuildEvent("GITHUB_PUSH_ERROR", { error: result.error });
                              toast.error(friendlyErrorMessage(result.error));
                            }
                          }}
                          disabled={githubPushRunning}
                        >
                          Push update
                        </Button>
                      </div>
                    )}
                    {!githubLinkedRepo && (
                      <>
                        <div className="space-y-2">
                          <Label>New repo name</Label>
                          <Input
                            placeholder={`DayOne-${(activeProject?.name || "project").replace(/[^a-zA-Z0-9-_]/g, "-")}`}
                            value={githubPushRepoName}
                            onChange={(e) => setGitHubPushRepoName(e.target.value)}
                            className="font-mono"
                          />
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={githubPushPrivate}
                            onChange={(e) => setGitHubPushPrivate(e.target.checked)}
                          />
                          Private repo
                        </label>
                        <Button
                          className="w-full"
                          onClick={handleCreateRepoAndPush}
                          disabled={githubPushRunning}
                        >
                          Create repo + Push
                        </Button>
                      </>
                    )}
                    <div className="border-t pt-4 space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">Or push to existing repo</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="owner"
                          value={githubPushExistingOwner}
                          onChange={(e) => setGitHubPushExistingOwner(e.target.value)}
                          className="font-mono flex-1"
                        />
                        <Input
                          placeholder="repo"
                          value={githubPushExistingRepo}
                          onChange={(e) => setGitHubPushExistingRepo(e.target.value)}
                          className="font-mono flex-1"
                        />
                      </div>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={handlePushToExisting}
                        disabled={githubPushRunning}
                      >
                        Push to existing
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pre-deploy API keys modal */}
      <Dialog open={preDeployModalOpen} onOpenChange={(open) => { if (!open) handlePreDeployKeysSkip(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              API Keys Needed
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Your app uses placeholder API keys. Add real keys before deploying, or deploy with placeholders and update later.
          </p>
          <div className="space-y-3 mt-2">
            {preDeployKeys.map((p) => (
              <div key={p.keyName}>
                <Label className="text-xs font-mono text-muted-foreground">{p.keyName} <span className="text-foreground/50">— {p.service} ({p.reason})</span></Label>
                <Input
                  placeholder={p.placeholder}
                  value={preDeployKeyValues[p.keyName] || ""}
                  onChange={(e) => setPreDeployKeyValues(prev => ({ ...prev, [p.keyName]: e.target.value }))}
                  className="mt-1 font-mono text-xs"
                  type="password"
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={handlePreDeployKeysSkip}>
              Deploy without keys
            </Button>
            <Button size="sm" onClick={handlePreDeployKeysSubmit}>
              Save keys & deploy
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deployDialogOpen} onOpenChange={(open) => {
        setDeployDialogOpen(open);
        if (!open) { resetDeploy(); resetDomain(); }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Deploy to Vercel</DialogTitle>
          </DialogHeader>

          {/* ── 3-phase progress indicator ── */}
          {deployStatus !== "idle" && (
            <div className="flex items-center gap-0 py-3">
              {([
                { key: "preparing", label: "Preparing" },
                { key: "deploying", label: "Deploying" },
                { key: "success", label: "Live" },
              ] as const).map((phase, i, arr) => {
                const phaseIndex = deployStatus === "error"
                  ? (deployStatus === "error" && i <= 1 ? 1 : -1)
                  : arr.findIndex(p => p.key === deployStatus);
                const isCurrent = i === phaseIndex || (deployStatus === "error" && i === 1);
                const isComplete = i < phaseIndex;
                const isErrorPhase = deployStatus === "error" && isCurrent;
                return (
                  <React.Fragment key={phase.key}>
                    {i > 0 && <div className={`flex-1 h-px mx-2 ${isComplete || isCurrent ? "bg-primary" : "bg-border"}`} />}
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-colors ${
                        isErrorPhase ? "border-destructive bg-destructive/10 text-destructive" :
                        isComplete ? "border-primary bg-primary text-primary-foreground" :
                        isCurrent ? "border-primary bg-primary/10 text-primary" :
                        "border-border bg-muted text-muted-foreground"
                      }`}>
                        {isErrorPhase ? <XCircle className="h-3.5 w-3.5" /> :
                         isComplete ? <Check className="h-3.5 w-3.5" /> :
                         isCurrent ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                         i + 1}
                      </div>
                      <span className={`text-sm ${isCurrent || isComplete ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                        {phase.label}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {/* ── Build logs ── */}
          {deployLogs.length > 0 && (
            <ScrollArea className="max-h-40 rounded-md border border-border bg-muted/30 p-3">
              <div className="space-y-1 font-mono text-xs text-muted-foreground">
                {deployLogs.map((log, i) => (
                  <p key={i}>{log}</p>
                ))}
              </div>
            </ScrollArea>
          )}

          {/* ── Success result ── */}
          {deployStatus === "success" && (
            <div className="space-y-4">
              <p className="text-green-600 dark:text-green-500 font-medium">Your app is live.</p>
              {deployResult?.url && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button asChild className="flex-1 gap-2">
                      <a href={deployResult.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                        Open App
                      </a>
                    </Button>
                    <Button
                      variant="secondary"
                      className="gap-2"
                      onClick={() => {
                        navigator.clipboard.writeText(deployResult?.url || "");
                        toast.success("URL copied to clipboard");
                      }}
                    >
                      <Copy className="h-4 w-4" />
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground break-all">{deployResult.url}</p>
                </div>
              )}

              {deployResult?.inspectUrl && (
                <div className="pt-2 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Details</p>
                  <a
                    href={deployResult.inspectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline break-all"
                  >
                    {deployResult.inspectUrl}
                  </a>
                </div>
              )}

              {/* ── Custom domain section ── */}
              <div className="pt-2 border-t border-border">
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full">
                    <Globe className="h-4 w-4" />
                    Custom Domain
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    {currentProject?.id && (
                      <CustomDomainPanel
                        projectId={currentProject.id}
                        hasDeployed={!!deployResult?.url}
                      />
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </div>

              <div className="flex gap-2 pt-2 border-t border-border">
                <Button onClick={handleDeploy} variant="outline" className="flex-1 gap-2">
                  <Loader2 className="h-4 w-4" />
                  Redeploy
                </Button>
                <Button
                  onClick={() => window.open(deployResult?.url, '_blank')}
                  variant="outline"
                  className="flex-1 gap-2"
                  disabled={!deployResult?.url}
                >
                  <ExternalLink className="h-4 w-4" />
                  Open App
                </Button>
                <Button
                  onClick={() => setDeployDialogOpen(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Done
                </Button>
              </div>
            </div>
          )}

          {/* ── Error result ── */}
          {deployStatus === "error" && (
            <div className="space-y-3">
              <p className="text-destructive font-medium">Deployment failed</p>
              {(deployResult?.error || deployResult?.message) && (
                <div className="rounded-md border border-destructive/20 bg-destructive/5 p-3">
                  <p className="text-sm text-foreground">
                    {deployResult.error || deployResult.message}
                  </p>
                  {deployResult.details && (
                    <p className="text-xs text-muted-foreground mt-1">{deployResult.details}</p>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={handleDeploy} variant="outline" className="flex-1">
                  Retry
                </Button>
                <Button onClick={() => setDeployDialogOpen(false)} variant="ghost" className="flex-1">
                  Close
                </Button>
              </div>
            </div>
          )}

          {/* ── Auto-deploy toggle ── */}
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div>
              <Label htmlFor="auto-deploy-toggle" className="text-sm font-medium">Auto-deploy on edit</Label>
              <p className="text-xs text-muted-foreground">Deploy after each successful build</p>
            </div>
            <Switch
              id="auto-deploy-toggle"
              checked={autoDeployEnabled}
              onCheckedChange={toggleAutoDeploy}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Paywall modal for upgrade prompts */}
      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        reason={paywallReason}
        currentTier={tier as "free" | "starter" | "pro"}
      />
    </>
  );
};

export default CodeWorkspace;
